import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import VoiceMicButton from '../components/VoiceMicButton';
import SpeakButton from '../components/SpeakButton';
import VoiceModeOverlay from '../components/VoiceModeOverlay';
import { stopText } from '../utils/voicePlayback';
import { AGENTS, AGENT_SUGGESTED_QUERIES, AGENT_QUICK_QUERIES } from '../data/agentCatalog';
import { Bot, Cpu, Database, FileText, Upload, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronRight, Sparkles, Terminal, Send, Zap, BookOpen, Wrench, BarChart3, HardHat, FileSpreadsheet, ShieldCheck, Search, Mail, Presentation, Paperclip, X, Settings2, Play, RefreshCw, LayoutDashboard, MoreHorizontal, ScrollText, FileDown, Leaf, Landmark, Globe, Plug, History, Clock, Trash2, Pin, PinOff } from 'lucide-react';
import usePinnedAgents from '../hooks/usePinnedAgents';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import { canAccessDigitalTwinDemo } from '../config/digitalTwinDemoAccess';

axios.defaults.withCredentials = true;

/** If plan `to` still shows demo addresses, fix using the logged-in user from AuthContext. */
function coerceJoboticsSendEmailTo(to, loginEmail, question) {
  if (!loginEmail) return to;
  const q = (question || '').trim();
  const wantsOnFile =
    /email (it )?to\s+my|my (email|address|inbox|account)\s+on\s+file|to\s+my\s+(email|address|inbox)(?:\s+on\s+file)?|address on file|my\s+email\s+on\s+file|email\s+on\s+file/i.test(q);
  if (wantsOnFile) return loginEmail;
  const BAD = new Set(['admin@sylvan.com', 'admin@example.com', 'user@company.com']);
  const parts = String(to || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return loginEmail;
  const mapped = parts.map((p) => (BAD.has(p.toLowerCase()) ? loginEmail : p));
  const seen = new Set();
  const out = [];
  for (const m of mapped) {
    const k = m.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(m);
    }
  }
  return out.join(', ');
}

/** Last-line fix if the model still echoes demo addresses in the final markdown. */
function scrubResultEmailText(text, loginEmail) {
  if (!text || !loginEmail) return text;
  let t = text;
  const BAD = ['admin@sylvan.com', 'admin@example.com', 'user@company.com'];
  for (const bad of BAD) {
    t = t.replace(new RegExp(bad.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), loginEmail);
  }
  return t;
}

const EXAMPLE_QUESTION_GROUPS = [
  {
    label: 'Labor & materials',
    icon: '🔧',
    questions: [
      'I uploaded a mechanical takeoff — estimate labor hours for each line and export to Excel',
      'Look up labor hours for an 8 inch carbon steel 90° long-radius elbow (single line item)',
      'I uploaded PDF drawings — extract a bill of materials with quantities into Excel',
    ],
  },
  {
    label: 'AEC · Trends & macro',
    icon: '📈',
    questions: [
      'Show me construction employment trends over the last 5 years',
      'How have material prices changed since 2020?',
      'Compare residential vs. non-residential construction spending',
      'What are building permit trends by region?',
    ],
  },
  {
    label: 'AEC · Labor & wages',
    icon: '👷',
    questions: [
      'What are prevailing wages for electricians in California?',
      'Show county-level construction employment in Texas',
      'Compare construction labor costs across MSAs',
    ],
  },
  {
    label: 'AEC · Infrastructure & awards',
    icon: '🌉',
    questions: [
      'How many bridges are structurally deficient in my state?',
      'Which states have the oldest bridge infrastructure?',
      'Show me federal construction awards over $10M in 2024',
    ],
  },
  {
    label: 'AEC · Materials & commodities',
    icon: '🧱',
    questions: [
      'Track PPI for steel, concrete, and lumber',
      'What commodities have seen the biggest price increases?',
    ],
  },
  {
    label: 'AEC · Emissions & energy',
    icon: '🌿',
    questions: [
      'Which construction-related facilities have highest GHG emissions?',
      'Show me energy consumption trends by state',
    ],
  },
  {
    label: 'AEC · Cross-dataset',
    icon: '🔗',
    questions: [
      'Correlate construction spending with employment growth',
      'Compare federal awards to permit activity by state',
      'Analyze wage trends vs. material price inflation',
    ],
  },
  {
    label: 'AEC · Dashboard, deck & email',
    icon: '📧',
    questions: [
      'Build an HTML dashboard with charts for the AEC metrics we just discussed',
      'Create a 5-slide executive deck summarizing this AEC analysis',
      'Email me at my login address a professional summary of the analysis above with bullet takeaways and key figures',
      'Send an email to my inbox with the competitive analysis: subject line, short executive summary, and links to any dashboard or files you created',
    ],
  },
  {
    label: 'Deliverables & exports',
    icon: '📤',
    questions: [
      'Create a short Word document outlining scope, assumptions, and risks from bullet notes I paste below',
      'Turn this analysis into a one-page PDF handout with clear sections',
      'Export this table to a formatted Excel workbook',
    ],
  },
  {
    label: 'Web research',
    icon: '🌐',
    questions: [
      'Research current trends in industrial construction labor productivity and cite sources',
      'Find reference photos of a specific fixture or installation detail using web search',
    ],
  },
];

/** Eight hero chips: natural-language prompts (no tool/MCP jargon in the text). */
const QUICK_TASKS = [
  {
    id: 'aec-trends-dashboard',
    label: 'AEC trends dashboard',
    Icon: LayoutDashboard,
    sample:
      'Show construction employment and spending over roughly the last five years, plus regional permit trends if useful. Build an interactive HTML dashboard titled “Construction macro snapshot” with charts, KPI cards, and a short interpretation.',
  },
  {
    id: 'aec-email-brief',
    label: 'AEC email brief',
    Icon: Mail,
    sample:
      'Give me a tight executive snapshot on construction jobs and spending, with one clear risk or opportunity. Email it to my address on file with subject “Construction executive brief” — bullets, key figures, and note the data sources and years.',
  },
  {
    id: 'aec-infra-deck',
    label: 'Infra awards deck',
    Icon: Landmark,
    sample:
      'Summarize bridge conditions and federal construction awards relevant to my state. Turn it into about five slides titled “Infrastructure outlook” for leadership.',
  },
  {
    id: 'aec-wages-excel',
    label: 'Wages → Excel',
    Icon: FileSpreadsheet,
    sample:
      'For a U.S. state I care about, show prevailing wages or regional construction employment in a small table and export it to a formatted Excel workbook with a clear sheet name.',
  },
  {
    id: 'aec-materials-pdf',
    label: 'Materials PDF',
    Icon: FileDown,
    sample:
      'Show PPI trends for steel, concrete, and lumber. Put the takeaways in a one-page PDF executive handout with sections, dates, and key numbers.',
  },
  {
    id: 'aec-macro-deck',
    label: 'Macro story deck',
    Icon: BarChart3,
    sample:
      'How does construction spending line up with employment growth (and permits vs awards if the data allows)? Turn the story into about five slides titled “Spending vs jobs” with speaker-ready notes.',
  },
  {
    id: 'aec-esg-dashboard',
    label: 'ESG dashboard',
    Icon: Leaf,
    sample:
      'Construction-related emissions or energy indicators by state or sector — build an HTML dashboard titled “ESG snapshot” with charts and a short narrative.',
  },
  {
    id: 'labor-takeoff-excel',
    label: 'Labor & takeoff',
    Icon: HardHat,
    sample:
      'I uploaded a mechanical takeoff — estimate labor hours for each line and export to Excel with labor columns and clean formatting.',
  },
].filter((t) => t.sample);

/**
 * Quick mode samples — aligned with main `AgentDemo.js` (MCAA / NECA / web).
 * Jobotics quick endpoint has no file search; uploads are hidden in the UI.
 */
const JOBOTICS_QUICK_MODE_LABOR_MCAA_SAMPLES = [
  'Labor hours: 10" carbon steel pipe, standard wall (MCAA / Weblem).',
  'Labor hours: 12" carbon steel pipe, butt welded.',
  'Compare labor hours for standard vs extra-strong wall on steel pipe.',
];

const JOBOTICS_QUICK_MODE_LABOR_NECA_SAMPLES = [
  'NECA labor: 100A plug-in on lighting busway.',
  'NECA labor units: ¾" EMT conduit.',
  'NECA labor: 400A temporary service equipment.',
];

const JOBOTICS_QUICK_MODE_BATCH_SAMPLES = [
  'Batch MCAA labor — one line per item:\n6" SCH 40 carbon steel pipe\n6" 90 LR elbow',
  'Batch NECA labor — one line per item:\n¾" EMT conduit\n20A duplex receptacle\nLED fixture rough-in',
];

const JOBOTICS_QUICK_MODE_RESEARCH_SAMPLES = [
  'Name three mechanical-contracting competitors and how they differ (size, markets, focus).',
  'What are prevailing wages for electricians in California? Summarize with sources.',
];

const JOBOTICS_QUICK_MODE_EXAMPLE_GROUPS = [
  { label: 'MCAA / Weblem (mechanical)', icon: '🔧', questions: JOBOTICS_QUICK_MODE_LABOR_MCAA_SAMPLES },
  { label: 'NECA (electrical)', icon: '⚡', questions: JOBOTICS_QUICK_MODE_LABOR_NECA_SAMPLES },
  { label: 'Batch labor (paste lines)', icon: '📋', questions: JOBOTICS_QUICK_MODE_BATCH_SAMPLES },
  { label: 'Web research', icon: '🌐', questions: JOBOTICS_QUICK_MODE_RESEARCH_SAMPLES },
];

/** Hero chips when Quick is on (same layout idea as main Agent Demo). */
const JOBOTICS_QUICK_MODE_TASK_CHIPS = [
  {
    id: 'mcaa',
    label: 'MCAA / Weblem',
    Icon: Wrench,
    sample: JOBOTICS_QUICK_MODE_LABOR_MCAA_SAMPLES[0],
  },
  {
    id: 'neca',
    label: 'NECA',
    Icon: Plug,
    sample: JOBOTICS_QUICK_MODE_LABOR_NECA_SAMPLES[0],
  },
  {
    id: 'batch',
    label: 'Batch labor',
    Icon: FileSpreadsheet,
    sample: JOBOTICS_QUICK_MODE_BATCH_SAMPLES[0],
  },
  {
    id: 'research',
    label: 'Web research',
    Icon: Globe,
    sample: JOBOTICS_QUICK_MODE_RESEARCH_SAMPLES[0],
  },
];

/** Index of first row in EXAMPLE_QUESTION_GROUPS whose label is an AEC use case (for section header in “More”). */
const FIRST_AEC_EXAMPLE_GROUP_INDEX = EXAMPLE_QUESTION_GROUPS.findIndex((g) => g.label.startsWith('AEC ·'));

const TOOL_ICONS = {
  estimate_labor_cost: HardHat,
  batch_estimate_labor_cost: HardHat,
  estimate_neca_labor_cost: HardHat,
  batch_estimate_neca_labor_cost: HardHat,
  generate_material_matrix: FileSpreadsheet,
  web_search: Search,
  send_email: Mail,
  generate_slides: Presentation,
  generate_excel: FileSpreadsheet,
  generate_word: ScrollText,
  generate_pdf: FileDown,
  generate_dashboard: LayoutDashboard,
  list_aec_mcp_tools: Database,
  call_aec_mcp_tool: Zap,
};

const TOOL_COLORS = {
  estimate_labor_cost: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  batch_estimate_labor_cost: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  estimate_neca_labor_cost: 'text-amber-700 bg-amber-50 border-amber-200',
  batch_estimate_neca_labor_cost: 'text-amber-700 bg-amber-50 border-amber-200',
  generate_material_matrix: 'text-teal-600 bg-teal-50 border-teal-200',
  web_search: 'text-lime-600 bg-lime-50 border-lime-200',
  send_email: 'text-blue-700 bg-blue-50 border-blue-300',
  generate_slides: 'text-amber-700 bg-amber-50 border-amber-300',
  generate_excel: 'text-green-700 bg-green-50 border-green-200',
  generate_word: 'text-sky-700 bg-sky-50 border-sky-200',
  generate_pdf: 'text-orange-700 bg-orange-50 border-orange-200',
  generate_dashboard: 'text-cyan-700 bg-cyan-50 border-cyan-200',
  list_aec_mcp_tools: 'text-slate-600 bg-slate-50 border-slate-200',
  call_aec_mcp_tool: 'text-violet-600 bg-violet-50 border-violet-200',
};

const TOOL_META = {
  estimate_labor_cost:  { label: 'Labor Hour Lookup',     summary: 'Looks up labor hours from the MCAA/Weblem mechanical database for a described work item.' },
  batch_estimate_labor_cost: { label: 'Batch labor estimate', summary: 'Batch labor lookup for many materials from a takeoff list or uploaded table.' },
  estimate_neca_labor_cost: { label: 'NECA labor lookup', summary: 'Electrical labor units from the NECA manual (neca.labor_units) for a described scope.' },
  batch_estimate_neca_labor_cost: { label: 'Batch NECA labor', summary: 'Batch NECA labor lookup for many electrical line items.' },
  send_email:           { label: 'Send Email',            summary: 'Sends email via O365/SMTP with styled HTML from Markdown.' },
  generate_slides:      { label: 'AI Slide Deck',         summary: 'PowerPoint (.pptx) from markdown — download link only.' },
  generate_excel:       { label: 'Excel (Claude xlsx)',   summary: 'Native Claude xlsx skill — formatted .xlsx with download link.' },
  generate_word:        { label: 'Word (native docx)',    summary: 'Native Claude docx skill — professional .docx download.' },
  generate_pdf:         { label: 'PDF (native)',          summary: 'Native Claude pdf skill — PDF download.' },
  generate_dashboard:   { label: 'HTML dashboard',        summary: 'Interactive HTML + Chart.js via code execution; optional public image URLs.' },
  generate_material_matrix: { label: 'Material Matrix', summary: 'Uploaded PDF drawings → BOM / takeoff Excel.' },
  web_search:           { label: 'Web Research & image discovery', summary: 'Claude web search — research topics and find reference images on the public web (not a private file index).' },
  list_aec_mcp_tools:   { label: 'AEC MCP — list tools', summary: 'Lists tools from the configured AEC MCP server (env AEC_MCP_URL).' },
  call_aec_mcp_tool:    { label: 'AEC MCP — call tool', summary: 'Runs a named tool on the AEC MCP server with JSON arguments.' },
};

const FRIENDLY_TOOL_NAMES = Object.fromEntries(Object.entries(TOOL_META).map(([k, v]) => [k, v.label]));

const FRIENDLY_PARAM_LABELS = {
  project_id: 'Project ID',
  year: 'Year',
  month: 'Month',
  date: 'Date',
  entity: 'Entity Type',
  group_by: 'Group By',
  keyword: 'Search Keyword',
  scope_description: 'Scope Description',
  contract_text: 'Contract Text',
  focus_areas: 'Focus Areas',
  query: 'Search Query',
  status: 'Status Filter',
  limit: 'Max Results',
  to: 'Recipient Email',
  subject: 'Subject',
  body: 'Body',
  content: 'Slide Content',
  title: 'Presentation Title',
  num_pages: 'Number of Pages',
  sql: 'SQL Query',
  explanation: 'Explanation',
};

function LiveTimer({ startTime }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  return <span className="text-xs text-blue-400 font-mono tabular-nums">{elapsed}s</span>;
}

/**
 * PauseModal — renders the agent's pause-and-ask card. The agent
 * called request_user_input; the server is holding its tool result
 * promise open until the user picks. Two visual flavors:
 *
 *   kind="gate"     — pre-commit checkpoint ("Proceed with this plan?")
 *                     Indigo border, ShieldCheck icon. Default = Proceed.
 *   kind="clarify"  — ambiguity, multiple plausible answers.
 *                     Amber border, MoreHorizontal-style "decide" icon.
 *
 * Notes textarea is always present (optional) so the user can attach
 * a reason ("use Schedule 80 because spec calls for high-pressure").
 */
/**
 * LargeFileUploadChips — staged direct-to-S3 file chips with live
 * progress, used by BOTH the hero composer (first message) AND the
 * follow-up composer. Previously the chip-rendering JSX was only
 * inlined in the hero — follow-up users could pick files via the
 * paperclip but got NO visual feedback (state updated, UI didn't),
 * leading to "I attached files but the agent said it can't see them"
 * complaints. Extracting once avoids that drift.
 *
 * Four chip states:
 *   queued    → slate, "filename · 3.2 MB · queued"  + remove button
 *   uploading → amber, with live progress bar + "N %"
 *   ready     → emerald, "filename · 3.2 MB ✓" + remove button
 *   error     → rose, "filename · failed" (hover title shows error)
 */
function LargeFileUploadChips({ uploads, onRemove }) {
  if (!uploads || uploads.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {uploads.map((u) => {
        const sizeMb = (u.size / (1024 * 1024)).toFixed(1);
        if (u.status === 'uploading') {
          return (
            <div
              key={u.tmpId}
              title={`${u.filename} — uploading to S3 (${u.progress}%)`}
              className="flex items-center gap-2 px-2.5 py-1 border border-amber-200 bg-amber-50 text-amber-800 rounded-lg text-xs min-w-[260px]"
            >
              <FileText className="w-3 h-3 flex-shrink-0" />
              <span className="truncate flex-1">{u.filename}</span>
              <span className="font-mono tabular-nums">{u.progress}%</span>
              <div className="w-16 h-1.5 bg-amber-200 rounded-full overflow-hidden flex-shrink-0">
                <div
                  className="h-full bg-amber-500 transition-all duration-150"
                  style={{ width: `${u.progress}%` }}
                />
              </div>
            </div>
          );
        }
        const tone =
          u.status === 'ready'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
            : u.status === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-700'
              : 'bg-slate-50 border-slate-300 text-slate-600'; // queued
        const label =
          u.status === 'ready'
            ? `${u.filename} · ${sizeMb} MB ✓`
            : u.status === 'error'
              ? `${u.filename} · failed`
              : `${u.filename} · ${sizeMb} MB · queued (uploads on Send)`;
        return (
          <div
            key={u.tmpId}
            title={u.status === 'error' ? u.error : u.status === 'queued' ? 'Will upload to S3 when you click Send' : `s3Key: ${u.s3Key || '(pending)'}`}
            className={`flex items-center gap-1.5 px-2 py-0.5 border rounded-lg text-xs ${tone}`}
          >
            <FileText className="w-3 h-3" />
            <span className="truncate max-w-[280px]">{label}</span>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(u.tmpId)}
                className="opacity-70 hover:opacity-100"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * PauseModal — rich answer affordances for an agent pause.
 *
 * Three input modes (any combination depending on payload flags):
 *   - Button options       — pause.options array (the default)
 *   - Free-text answer     — when pause.allow_free_text or NO options
 *   - File upload          — when pause.allow_file_upload (uses the
 *                            same direct-to-S3 presign flow as the
 *                            chat composer; returns the s3Key as the
 *                            user's answer)
 *
 * Always shows a notes textarea so the user can attach reasoning to
 * any choice (e.g. "use Schedule 80 because spec calls for high-
 * pressure"). Submits via onSubmit({ choice, notes, s3Key? }).
 */
function PauseModal({ pause, submitting, onSubmit, onCancelRun }) {
  const [notes, setNotes] = useState('');
  const [freeText, setFreeText] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedS3Key, setUploadedS3Key] = useState(null);
  const [uploadErr, setUploadErr] = useState(null);

  const isGate = pause.kind === 'gate';
  const hasOptions = Array.isArray(pause.options) && pause.options.length > 0;
  const allowFreeText = pause.allow_free_text === true || !hasOptions;
  const allowFileUpload = pause.allow_file_upload === true;

  const tone = isGate
    ? 'border-indigo-400 bg-indigo-50'
    : 'border-amber-400 bg-amber-50';
  const headerTone = isGate ? 'text-indigo-900' : 'text-amber-900';

  // Direct-to-S3 upload mirroring the chat composer path. We presign,
  // PUT the file, then return the s3Key as the user's answer.
  const onPickFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploadingFile(true); setUploadErr(null);
    try {
      const presign = await fetch('/api/large-file/presign', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: f.name, contentType: f.type || 'application/octet-stream', size: f.size }),
      });
      if (!presign.ok) {
        const body = await presign.json().catch(() => ({}));
        throw new Error(body.error || `presign failed (HTTP ${presign.status})`);
      }
      const { uploadUrl, s3Key } = await presign.json();
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': f.type || 'application/octet-stream' },
        body: f,
      });
      if (!putRes.ok) throw new Error(`S3 PUT failed (HTTP ${putRes.status})`);
      setUploadedS3Key(s3Key);
    } catch (e2) {
      setUploadErr(e2.message);
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    // Sticky banner — pinned to the top of the chat so a user scrolling
    // through activity can't miss it. z-20 keeps it above the activity
    // log + step items without going over the global header.
    <div className="sticky top-0 z-20 mb-3">
      <div className={`${tone} border-2 rounded-lg px-4 py-4 shadow-lg`}>
        <div className="flex items-baseline gap-2 mb-2">
          {isGate
            ? <ShieldCheck className="w-5 h-5 text-indigo-700 self-center" />
            : <MoreHorizontal className="w-5 h-5 text-amber-700 self-center" />}
          <span className={`text-sm font-bold ${headerTone}`}>
            {isGate ? 'The agent wants to confirm before continuing' : 'The agent needs your input to continue'}
          </span>
          <span className="ml-auto text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-full px-2 py-0.5">
            Paused — agent is waiting
          </span>
        </div>
        {pause.summary && (
          <div className="text-xs text-slate-700 mb-2 ml-7 italic">{pause.summary}</div>
        )}
        <div className="text-base font-medium text-slate-900 ml-7 mb-3">{pause.question}</div>

        {/* Button options (when supplied) */}
        {hasOptions && (
          <div className="ml-7 space-y-2 mb-3">
            {pause.options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={submitting}
                onClick={() => onSubmit(opt.value, notes)}
                className={`w-full text-left border rounded-md px-3 py-2 transition disabled:opacity-50 ${
                  pause.default === opt.value
                    ? 'border-indigo-500 bg-white hover:bg-indigo-50 ring-1 ring-indigo-300'
                    : 'border-slate-300 bg-white hover:bg-slate-50'
                }`}
              >
                <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                {opt.description && <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>}
              </button>
            ))}
          </div>
        )}

        {/* Free-text answer */}
        {allowFreeText && (
          <div className="ml-7 mb-3">
            <label className="text-xs font-medium text-slate-700">Or type your answer</label>
            <div className="flex gap-2 mt-1">
              <input
                type="text"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                maxLength={500}
                placeholder="e.g. NEC Article 250"
                className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
                onKeyDown={(e) => { if (e.key === 'Enter' && freeText.trim() && !submitting) onSubmit('text', notes, { text: freeText.trim() }); }}
              />
              <button
                type="button"
                disabled={submitting || !freeText.trim()}
                onClick={() => onSubmit('text', notes, { text: freeText.trim() })}
                className="rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-3 py-1.5 shadow-sm disabled:opacity-50"
              >
                Send answer
              </button>
            </div>
          </div>
        )}

        {/* File upload */}
        {allowFileUpload && (
          <div className="ml-7 mb-3">
            <label className="text-xs font-medium text-slate-700">Or upload a file</label>
            {!uploadedS3Key ? (
              <div className="mt-1">
                <input
                  type="file"
                  onChange={onPickFile}
                  disabled={uploadingFile || submitting}
                  className="text-xs"
                />
                {uploadingFile && <div className="text-xs text-slate-500 mt-1">Uploading…</div>}
                {uploadErr && <div className="text-xs text-rose-700 mt-1">Upload failed: {uploadErr}</div>}
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                  ✓ File uploaded
                </span>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => onSubmit('file', notes, { s3Key: uploadedS3Key })}
                  className="rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-3 py-1.5 shadow-sm disabled:opacity-50"
                >
                  Send file as answer
                </button>
              </div>
            )}
          </div>
        )}

        {/* Always-on notes textarea */}
        <div className="ml-7 mb-3">
          <label className="text-xs font-medium text-slate-700">
            {hasOptions ? 'Optional note (attached to your choice above)' : 'Optional reasoning for the agent'}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. spec calls for Schedule 80 on any steam line"
            className="w-full mt-1 rounded border border-slate-300 px-2 py-1 text-xs focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
          />
        </div>

        {/* Escape hatches */}
        <div className="ml-7 flex items-center justify-between text-xs">
          <span className="text-slate-500">Auto-cancels after 10 min of no answer.</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={submitting}
              onClick={() => onSubmit('cancel', notes)}
              className="text-slate-600 hover:text-slate-900 underline"
            >
              Cancel this question
            </button>
            {onCancelRun && (
              <button
                type="button"
                onClick={onCancelRun}
                className="text-rose-600 hover:text-rose-800 underline"
              >
                Stop the whole run
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * EditableRowTable — renders agent output rows with inline-editable
 * cells. Each save POSTs to /api/agent-corrections, capturing the
 * (original, corrected, field_name) tuple plus the run_id + agent_id
 * for downstream calibration.
 *
 * Confidence column is rendered from the row's _confidence metadata
 * (set by the accumulator's heuristic or by the agent's Python code).
 * Click any value cell to edit it.
 */
function EditableRowTable({ rows, agentId, runId }) {
  const [editing, setEditing] = useState(null); // {i, key}
  const [draft, setDraft] = useState('');
  const [savedKey, setSavedKey] = useState(null); // visual flash on success
  const [errorKey, setErrorKey] = useState(null);

  // Compute the column set from the first row — but filter out the
  // underscore-prefixed Trust Layer metadata so it doesn't appear as
  // a regular column. We render _confidence as a leading badge instead.
  const allKeys = Object.keys(rows[0] || {});
  const dataKeys = allKeys.filter((k) => !k.startsWith('_'));

  const CONF_TONE = {
    high:   'bg-emerald-100 text-emerald-800 border-emerald-200',
    medium: 'bg-amber-100 text-amber-800 border-amber-200',
    low:    'bg-rose-100 text-rose-800 border-rose-200',
  };

  const beginEdit = (i, key, currentValue) => {
    setEditing({ i, key });
    setDraft(currentValue == null ? '' : String(currentValue));
    setErrorKey(null);
  };
  const cancelEdit = () => { setEditing(null); setDraft(''); };
  const commitEdit = async () => {
    if (!editing) return;
    const { i, key } = editing;
    const original = rows[i][key];
    const corrected = draft;
    if (String(original ?? '') === corrected) { cancelEdit(); return; }  // no-op
    try {
      const r = await fetch('/api/agent-corrections', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId: runId || null,
          agentId: agentId || 'unknown',
          findingId: `row-${i}`,
          fieldName: key,
          original,
          corrected,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${r.status}`);
      }
      // Optimistically update the local row so the user sees their edit
      // stick. The real source of truth is the corrections table.
      rows[i][key] = corrected;
      setSavedKey(`${i}:${key}`);
      setTimeout(() => setSavedKey(null), 1500);
      cancelEdit();
    } catch (e) {
      setErrorKey(`${i}:${key}`);
    }
  };

  return (
    <table className="text-xs border border-gray-200 rounded">
      <thead>
        <tr className="bg-gray-50">
          <th className="px-2 py-1 text-left font-medium text-gray-600 border-b w-16">Confidence</th>
          {dataKeys.map((k) => (
            <th key={k} className="px-2 py-1 text-left font-medium text-gray-600 border-b">{k}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-gray-100">
            <td className="px-2 py-1">
              {row._confidence ? (
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${CONF_TONE[row._confidence] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${row._confidence === 'high' ? 'bg-emerald-500' : row._confidence === 'medium' ? 'bg-amber-500' : 'bg-rose-500'}`}/>
                  {row._confidence}
                </span>
              ) : <span className="text-slate-300 text-[10px]">—</span>}
            </td>
            {dataKeys.map((k) => {
              const cellKey = `${i}:${k}`;
              const isEditing = editing && editing.i === i && editing.key === k;
              const saved = savedKey === cellKey;
              const failed = errorKey === cellKey;
              return (
                <td key={k} className={`px-2 py-1 whitespace-nowrap ${saved ? 'bg-emerald-50' : ''} ${failed ? 'bg-rose-50' : ''}`}>
                  {isEditing ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        autoFocus
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                        onBlur={commitEdit}
                        className="border border-indigo-300 rounded px-1 py-0.5 text-xs min-w-[5rem]"
                      />
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => beginEdit(i, k, row[k])}
                      title="Click to edit (saves as correction)"
                      className="text-gray-700 text-left hover:bg-indigo-50 rounded px-0.5 -mx-0.5"
                    >
                      {String(row[k] ?? '')}{saved && <span className="ml-1 text-emerald-600">✓</span>}{failed && <span className="ml-1 text-rose-600">!</span>}
                    </button>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StepItem({ step, isLast }) {
  const [expanded, setExpanded] = useState(false);
  // Once a running step exceeds 60s, the original estimate ("10s – a
  // few min") becomes misleading. Track local elapsed time so we can
  // swap to a long-running hint that doesn't make the user think the
  // run is stuck.
  const [longRunning, setLongRunning] = useState(false);
  useEffect(() => {
    if (step.status !== 'running' || !step.startedAt) return;
    const id = setInterval(() => {
      if (Date.now() - step.startedAt > 60000) setLongRunning(true);
    }, 1000);
    return () => clearInterval(id);
  }, [step.status, step.startedAt]);
  const Icon = step.tool ? (TOOL_ICONS[step.tool] || Database) : (
    step.id === 'load-skill' ? BookOpen :
    step.id === 'build-context' ? Wrench :
    step.id === 'parse-file' ? FileText :
    step.id === 'summarize' ? Sparkles : Cpu
  );
  const colorClass = step.tool ? TOOL_COLORS[step.tool] : 'text-gray-600 bg-gray-50 border-gray-200';
  const statusIcon = step.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> :
                     step.status === 'error' ? <XCircle className="w-4 h-4 text-red-500" /> :
                     <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;

  const hasExpandable = step.sql || (step.rows && step.rows.length > 0) || step.resultPreview;

  return (
    <div className="relative">
      {!isLast && (
        <div className="absolute left-[19px] top-10 bottom-0 w-px bg-gray-200" />
      )}
      <div className="flex items-start gap-3 py-2">
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center ${colorClass}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {statusIcon}
            <span className="text-sm font-medium text-gray-900">{step.label}</span>
            {step.status === 'running' && step.startedAt && (
              <LiveTimer startTime={step.startedAt} />
            )}
            {step.status === 'running' && step.estimate && !longRunning && (
              <span className="text-xs text-gray-400 font-normal">est. {step.estimate}</span>
            )}
            {step.status === 'running' && longRunning && (
              <span className="text-xs text-amber-600 font-normal">still working — large files can take a few minutes</span>
            )}
            {step.duration && step.status === 'done' && (
              <span className="text-xs text-emerald-500 font-normal">{step.duration}</span>
            )}
            {step.duration && step.status === 'error' && (
              <span className="text-xs text-red-400 font-normal">{step.duration}</span>
            )}
          </div>
          {step.detail && (
            <p className="text-xs text-gray-500 mt-0.5 ml-6 whitespace-pre-wrap">{step.detail}</p>
          )}
          {/* Trust Layer: per-bucket confidence chips on finalize steps
              (and any future tool that returns a confidenceSummary).
              Green / amber / rose pills sized to the count distribution
              so a 8500/12/0 spread reads "mostly good, 12 to review,
              none failing" at a glance — no need to open the Excel. */}
          {step.confidenceSummary && (
            <div className="mt-1 ml-6 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-slate-500 uppercase tracking-wide">Trust:</span>
              {step.confidenceSummary.high > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {step.confidenceSummary.high.toLocaleString()} high
                </span>
              )}
              {step.confidenceSummary.medium > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {step.confidenceSummary.medium.toLocaleString()} medium
                </span>
              )}
              {step.confidenceSummary.low > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-100 text-rose-800 border border-rose-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  {step.confidenceSummary.low.toLocaleString()} low — review
                </span>
              )}
            </div>
          )}
          {/* Persistent reasoning trace — the model's "Results analyzed"
              step now carries the full thought text. Show truncated by
              default if the detail isn't already showing it; let the
              user expand for the full block. Without this, past
              reasoning was lost as the streamPreview got replaced by
              each new turn. */}
          {step.thinking && step.thinking !== step.detail && (
            <details className="mt-1 ml-6">
              <summary className="text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer">
                Show full reasoning ({(step.thinking.length / 1000).toFixed(1)}K chars)
              </summary>
              <pre className="mt-1 p-2 bg-indigo-50/40 border border-indigo-200 text-[11px] text-slate-800 rounded whitespace-pre-wrap break-words max-h-80 overflow-y-auto font-sans">
{step.thinking}
              </pre>
            </details>
          )}
          {step.link && (
            <div className="mt-1 ml-6 flex items-center gap-2 flex-wrap">
              <a
                href={step.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Presentation className="w-3.5 h-3.5" />
                {step.linkLabel || 'Open'}
              </a>
              {step.downloadUrl && step.downloadUrl !== step.link && (
                <a
                  href={step.downloadUrl}
                  download
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Download
                </a>
              )}
              {step.studioUrl && (
                <a
                  href={step.studioUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <Wrench className="w-3.5 h-3.5" />
                  {step.studioLabel || 'Open in Studio'}
                </a>
              )}
              {step.exportUrl && (
                <a
                  href={step.exportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Download PPTX
                </a>
              )}
            </div>
          )}
          {step.emailSent && (
            <div className="mt-1 ml-6 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Email delivered successfully
            </div>
          )}
          {hasExpandable && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mt-1 ml-6"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {step.sql ? 'Show SQL' : 'Show data'}
            </button>
          )}
          {expanded && step.sql && (
            <pre className="mt-2 ml-6 p-3 bg-gray-900 text-green-400 text-xs rounded-lg overflow-x-auto font-mono">{step.sql}</pre>
          )}
          {expanded && step.rows && step.rows.length > 0 && (
            <div className="mt-2 ml-6 overflow-x-auto">
              <EditableRowTable rows={step.rows} agentId={step._agentId} runId={step._runId} />
              <div className="text-[10px] text-slate-400 mt-1">
                Edits save as <em>corrections</em> — each one feeds the agent's confidence calibration. See the agent's <strong>Stats</strong> tab.
              </div>
            </div>
          )}
          {expanded && !step.rows && step.resultPreview && (
            <pre className="mt-2 ml-6 p-3 bg-slate-50 border border-slate-200 text-slate-800 text-[11px] rounded-lg overflow-x-auto whitespace-pre-wrap break-words font-mono max-h-96">
{step.resultPreview}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function ParamForm({ plan, editedParams, setEditedParams, onConfirm, onSkip, isRunning }) {
  const Icon = TOOL_ICONS[plan.tool_name] || Settings2;
  const color = TOOL_COLORS[plan.tool_name] || 'text-gray-600 bg-gray-50 border-gray-200';
  const friendlyName = FRIENDLY_TOOL_NAMES[plan.tool_name] || plan.tool_name;
  const schema = plan.schema;
  const properties = schema?.properties || {};
  const required = schema?.required || [];

  const visibleParams = Object.entries(properties).filter(
    ([key]) => !['sql', 'explanation'].includes(key)
  );

  if (visibleParams.length === 0) return null;

  return (
    <div className="bg-white border-2 border-indigo-200 rounded-2xl p-5 mb-6 shadow-sm animate-in fade-in slide-in-from-top-2">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{friendlyName}</h3>
            <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">AI Selected</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{plan.reasoning}</p>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        {visibleParams.map(([key, propDef]) => {
          const isRequired = required.includes(key);
          const label = FRIENDLY_PARAM_LABELS[key] || key;
          const value = editedParams[key] ?? '';
          const isTextArea = key === 'contract_text' || key === 'body' || key === 'content';
          const isEnum = propDef.enum;

          return (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {label}
                {isRequired && <span className="text-red-400 ml-0.5">*</span>}
                {propDef.description && (
                  <span className="font-normal text-gray-400 ml-1">— {propDef.description}</span>
                )}
              </label>
              {isEnum ? (
                <select
                  value={value}
                  onChange={e => setEditedParams(prev => ({ ...prev, [key]: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  <option value="">Select...</option>
                  {propDef.enum.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : isTextArea ? (
                <textarea
                  value={value}
                  onChange={e => setEditedParams(prev => ({ ...prev, [key]: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                  placeholder={propDef.description || ''}
                />
              ) : (
                <input
                  type={propDef.type === 'number' ? 'number' : 'text'}
                  value={value}
                  onChange={e => setEditedParams(prev => ({
                    ...prev,
                    [key]: propDef.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value
                  }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={propDef.description || ''}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onConfirm}
          disabled={isRunning}
          className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors"
        >
          <Play className="w-4 h-4" />
          Run with these parameters
        </button>
        <button
          onClick={onSkip}
          disabled={isRunning}
          className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
        >
          Skip — let AI decide
        </button>
      </div>
    </div>
  );
}

const JoboticsAgent = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // The agent persona is selected by `?agentId=<slug>` in the URL — set by
  // each card in the role-driven Home view. Unknown / missing slug falls back
  // to the generic Jobotics Agent (the existing behavior, unchanged).
  const agentId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return (sp.get('agentId') || '').trim().toLowerCase();
  }, [location.search]);
  // agentMeta resolution: built-in agents come from the static AGENTS
  // catalog (synchronous, fast). User-created custom agents live in
  // Postgres and aren't in that array — so on a catalog miss we lazy-
  // fetch their definition from /api/rsi-agents/:id and use that for
  // the page header + suggested queries.
  const builtIn = useMemo(
    () => AGENTS.find((a) => a.id === agentId) || null,
    [agentId]
  );
  const [userAgentMeta, setUserAgentMeta] = useState(null);
  useEffect(() => {
    if (!agentId || builtIn) { setUserAgentMeta(null); return; }
    let cancelled = false;
    fetch(`/api/rsi-agents/${encodeURIComponent(agentId)}`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data?.agent) return;
        const a = data.agent;
        setUserAgentMeta({
          id: a.id,
          name: a.name,
          // No emoji column yet — pick a brain emoji as the universal
          // marker for "user-built MEP agent."
          emoji: '\u{1F9E0}',
          category: a.category,
          blurb: a.description || `Custom ${a.task_type} agent.`,
          // Carry the suggested-queries list through so the empty-state
          // chips render the user's own examples.
          suggestedQueries: Array.isArray(a.suggested_queries) ? a.suggested_queries : [],
          isUserAgent: true,
        });
      })
      .catch(() => { /* fall back to generic header */ });
    return () => { cancelled = true; };
  }, [agentId, builtIn]);
  const agentMeta = builtIn || userAgentMeta;
  const agentSuggestedQueries = AGENT_SUGGESTED_QUERIES[agentId]
    || (userAgentMeta?.suggestedQueries?.length ? userAgentMeta.suggestedQueries : null);
  const agentQuickQueries = AGENT_QUICK_QUERIES[agentId] || null;

  // Large-file mode gates the direct-to-S3 presigned upload path (5 GB
  // cap) and the largeFileUploads chip UI. We trigger it for the two
  // built-in heavy-doc agents AND for every user-created RSI agent —
  // those need the same upload pipeline because their tools (pdf_*,
  // file_*, run_python_*) all read from S3.
  // Direct-to-S3 upload mode. Required for agents whose tools operate
  // on an s3Key — without this, files go via Multer and we only get
  // EXTRACTED TEXT injected into the prompt, with no s3Key for the
  // agent to feed to its pdf_info / pdf_search / pdf_read_pages /
  // pdf_ocr_pages / run_python_extract / markup_drawing_pdf tools.
  //
  // Audited list — every agent here works with large drawing/spec/
  // submittal PDFs in its persona. Agents NOT on the list (labor_estimator, mep_estimator, material_matrix_mep — which uses
  // its OWN generate_material_matrix flow — mentor, etc.) keep the
  // Multer path because their tools take text, not s3Keys.
  const LARGE_FILE_AGENTS = new Set([
    'large_file',
    // contract_review sweeps whole contracts via contract_sweep, which reads
    // from S3 — on the Multer path it would get extracted text with no s3Key,
    // and a 500 MB cap it does not need.
    'contract_review',
    'rsi_extractor',
    'drawing_markup',
    'field_markup',
    'mep_takeoff',
    'equipment_schedule_extractor',
    'single_line_drawings',
    'summary_spec_submittal',
    'deep_dive_spec_submittal',
    'prequalification',
  ]);
  const isLargeFileMode = LARGE_FILE_AGENTS.has(agentId) || !!userAgentMeta;
  // Wide-format mode gates the multi-MIME accept regex (Office, IFC,
  // DWF, etc.). large_file stays PDF-only because its tools are
  // PyMuPDF-only; rsi_extractor + custom agents get the wide set.
  const isWideFormatMode = agentId === 'rsi_extractor' || agentId === 'drawing_markup' || !!userAgentMeta;
  // Pinned-agents hook — drives the header Pin button. Pinning persists
  // to localStorage and the sidebar re-reads it on the next render.
  const { isPinned, togglePin } = usePinnedAgents();
  const currentPinned = agentMeta ? isPinned(agentMeta.id) : false;
  // Bare /jobotics-agent (no agentId) redirects to the role-driven Home.
  // (Computed here, returned at the end of the hooks section to avoid
  // breaking React's Rules of Hooks ordering.)
  const shouldRedirectToHome = !agentId && location.pathname === '/jobotics-agent';
  const [input, setInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [editedParams, setEditedParams] = useState({});
  const [messages, setMessages] = useState([]);
  const [currentSteps, setCurrentSteps] = useState([]);
  const [streamPreview, setStreamPreview] = useState('');
  // Trust Layer Phase B: the agent's profile decision, captured from
  // the `profile_decision` SSE event. Surfaced as a banner above the
  // activity stream so the user can SEE what the agent decided to do
  // (and abort if it picked the wrong strategy).
  const [profileDecision, setProfileDecision] = useState(null);
  // Trust Layer Phase C + true gates: when the agent calls
  // request_user_input, server emits `pause_request` SSE event. We
  // pop a blocking modal until the user picks. State holds the
  // current pause's payload (null when not paused).
  const [pendingPause, setPendingPause] = useState(null);
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  // AbortController for the in-flight streaming fetch — used by the
  // PauseModal's "Stop the whole run" affordance and any future
  // user-initiated cancel surface.
  const sseControllerRef = useRef(null);
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]);
  // Foldable Suggested-follow-ups chip group — user can collapse to
  // reclaim screen space when reading long agent results. Defaults
  // EXPANDED on the first set (so new users discover the chips) and
  // automatically re-expands every time a new set arrives.
  const [followUpsOpen, setFollowUpsOpen] = useState(true);
  useEffect(() => {
    if (followUpSuggestions.length > 0) setFollowUpsOpen(true);
  }, [followUpSuggestions]);
  // Prefill from the Home hero composer / quick-prompt chips. They navigate
  // here with the typed text in router state (initialQuestion). We drop it
  // into the input once on mount and clear the history state so a refresh or
  // back-nav doesn't re-inject it. We prefill (not auto-run) — some agents
  // need an upload first, so the user reviews + hits send.
  const initialQuestionApplied = useRef(false);
  useEffect(() => {
    if (initialQuestionApplied.current) return;
    const q = location.state?.initialQuestion;
    if (q && typeof q === 'string') {
      initialQuestionApplied.current = true;
      setInput(q);
      // Strip it from history so it doesn't persist across reloads.
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [compareFiles, setCompareFiles] = useState([]);
  // Large File Agent uploads go DIRECT to S3 via a presigned PUT URL
  // (the demo's Multer can't take a 1 GB body, but S3 happily can).
  // Shape per entry:
  //   { tmpId, filename, size, status: 'queued'|'uploading'|'ready'|'error',
  //     progress: 0-100, s3Key?, error? }
  // The s3Key is filled in only after both the PUT and the /register
  // confirmation succeed — the agent's pdf_* tools take that s3Key as
  // input and call PyMuPDF/Textract server-side. See largeFile route.
  const [largeFileUploads, setLargeFileUploads] = useState([]);
  // PARALLEL REF for the s3Keys that have completed. We can't rely on
  // largeFileUploads inside async handleSubmit / executeAgent — when
  // uploads finish during the same handler invocation, the in-flight
  // closure still sees the pre-upload state value (React batches
  // updates, the next render has the new value but the running async
  // function does not). Updating this ref SYNCHRONOUSLY alongside the
  // setLargeFileUploads call means executeAgent always reads
  // up-to-date s3Keys regardless of closure timing.
  const largeFileReadyRef = useRef([]); // [{ tmpId, filename, size, s3Key }]
  const [compareMode, setCompareMode] = useState(false);
  const [disabledTools, setDisabledTools] = useState([]);
  const [showMoreExamples, setShowMoreExamples] = useState(false);
  const [quickMode, setQuickMode] = useState(false);
  const quickModeRef = useRef(false);
  // Voice mode: a separate full-screen overlay (VoiceModeOverlay) that opens
  // a bidirectional OpenAI Realtime conversation. The mic button below the
  // text input is unrelated — that's dictation-only.
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  const joboticsHistorySessionDbIdRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySessions, setHistorySessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeHistoryPrefixedId, setActiveHistoryPrefixedId] = useState(null);
  const messagesEndRef = useRef(null);
  const latestBotRef = useRef(null);
  const liveStepsRef = useRef(null);
  const streamPreviewScrollRef = useRef(null);
  const inputRef = useRef(null);
  const heroInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const compareFileInputRef = useRef(null);

  const resetAgent = () => {
    joboticsHistorySessionDbIdRef.current = null;
    setActiveHistoryPrefixedId(null);
    setMessages([]);
    setCurrentSteps([]);
    setStreamPreview('');
    setFollowUpSuggestions([]);
    setPlan(null);
    setEditedParams({});
    setInput('');
    setAttachedFiles([]);
    setCompareFiles([]);
    setCompareMode(false);
    // Large File uploads belong to the prior session — clear so the
    // next chat doesn't auto-prepend stale s3Keys to the question.
    // (We don't delete the S3 objects; user can re-pin via the catalog
    // if they want to keep using the same uploads.)
    setLargeFileUploads([]);
    largeFileReadyRef.current = [];
    inputRef.current?.focus();
  };

  /** Cookie session auth works via axios.defaults.withCredentials; only add Bearer when present. */
  const authBearerHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const persistJoboticsTurn = useCallback(async ({ query: q, assistantText, steps, quick, via, agentId: turnAgentId }) => {
    try {
      const res = await axios.post(
        '/api/user-chat/jobotics/persist-turn',
        {
          sessionDbId: joboticsHistorySessionDbIdRef.current,
          userMessage: q,
          assistantMessage: assistantText,
          assistantMeta: { steps },
          quickMode: quick,
          // 'text' (default) or 'voice' — server stores this in metadata so
          // history rendering can flag voice turns with a mic affordance.
          via: via || 'text',
          // The active agent persona at the time of the turn (e.g.
          // 'labor_estimator'). Lets the History panel show which agent
          // drove which answer.
          agentId: turnAgentId || null,
        },
        { ...authBearerHeaders(), withCredentials: true }
      );
      if (res.data?.success && res.data.sessionDbId != null) {
        joboticsHistorySessionDbIdRef.current = res.data.sessionDbId;
        setActiveHistoryPrefixedId(res.data.prefixedSessionId || `chat_${res.data.sessionDbId}`);
      }
    } catch (err) {
      console.warn('[jobotics] persist-turn failed:', err.response?.data?.error || err.message);
    }
  }, []);

  const fetchJoboticsHistoryList = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        '/api/user-chat/sessions?sessionType=jobotics_agent&limit=40&page=1',
        authBearerHeaders()
      );
      if (res.data?.success) setHistorySessions(res.data.sessions || []);
    } catch {
      setHistorySessions([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (historyOpen) fetchJoboticsHistoryList();
  }, [historyOpen]);

  const loadHistorySessionMessages = async (prefixedId) => {
    try {
      const res = await axios.get(
        `/api/user-chat/sessions/${encodeURIComponent(prefixedId)}/messages`,
        authBearerHeaders()
      );
      if (!res.data?.success || !Array.isArray(res.data.messages)) return;
      const mapped = res.data.messages.map((m) => {
        const meta = m.metadata && typeof m.metadata === 'object' ? m.metadata : {};
        const steps = meta.steps || undefined;
        return {
          id: m.id,
          type: m.role === 'user' ? 'user' : 'bot',
          content: scrubResultEmailText(m.content, user?.email),
          timestamp: m.timestamp,
          steps,
          error: false,
        };
      });
      setMessages(mapped);
      const nid = parseInt(String(prefixedId).replace(/^chat_/, ''), 10);
      joboticsHistorySessionDbIdRef.current = Number.isFinite(nid) ? nid : null;
      setActiveHistoryPrefixedId(prefixedId);
      setHistoryOpen(false);
      setPlan(null);
      setFollowUpSuggestions([]);
      setCurrentSteps([]);
      setStreamPreview('');
    } catch {
      /* ignore */
    }
  };

  const deleteJoboticsHistorySession = async (prefixedId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this conversation from your saved Jobotics history?')) return;
    try {
      await axios.delete(`/api/user-chat/sessions/${encodeURIComponent(prefixedId)}`, authBearerHeaders());
      const deletedNum = parseInt(String(prefixedId).replace(/^chat_/, ''), 10);
      setHistorySessions((prev) => prev.filter((s) => s.id !== prefixedId));
      setActiveHistoryPrefixedId((prev) => (prev === prefixedId ? null : prev));
      if (joboticsHistorySessionDbIdRef.current === deletedNum) {
        joboticsHistorySessionDbIdRef.current = null;
        setMessages([]);
      }
    } catch {
      /* ignore */
    }
  };

  const formatHistoryTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return '';
    }
  };
  const savedQuestionRef = useRef('');

  /**
   * Upload a single PDF directly to S3 via a presigned URL. The Node
   * server is bypassed entirely — only the small `presign` and
   * `register` requests hit it. The actual bytes go straight from the
   * browser to S3 over HTTPS, which is what makes 1 GB+ files
   * tractable (Multer can't take that without OOM).
   *
   * Reports upload progress via the onProgress callback so the chip
   * row can show "32 %" while the PUT is in flight. Returns
   * { s3Key, size } on success, throws on any failure (the caller
   * sets status: 'error' so the user can retry).
   *
   * XMLHttpRequest used here (not fetch) because fetch doesn't expose
   * upload progress events on browsers we care about; XHR's upload
   * progress is universally supported.
   */
  const uploadFileToS3 = async (file, onProgress) => {
    const t0 = Date.now();
    console.log(`[large_file] uploadFileToS3 start: ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
    // 1. Presign
    const presignRes = await fetch('/api/large-file/presign', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      },
      credentials: 'include',
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || 'application/pdf',
        size: file.size,
      }),
    });
    if (!presignRes.ok) {
      const body = await presignRes.text();
      throw new Error(`presign failed: ${presignRes.status} ${body.slice(0, 200)}`);
    }
    const { uploadUrl, s3Key } = await presignRes.json();
    if (!uploadUrl || !s3Key) throw new Error('presign missing uploadUrl or s3Key');
    console.log(`[large_file] presign OK (${Date.now() - t0}ms): s3Key=${s3Key}`);
    console.log(`[large_file] PUT to: ${uploadUrl.split('?')[0]}...`);

    // 2. PUT to S3 with progress
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      // Timeout the XHR after 30 min so we don't hang forever if the
      // connection silently stalls (CSP or CORS issues sometimes
      // present as a request that never fires onload OR onerror).
      xhr.timeout = 30 * 60 * 1000;
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/pdf');
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && onProgress) {
          onProgress(Math.round((ev.loaded / ev.total) * 100));
        }
      };
      xhr.onload = () => {
        console.log(`[large_file] PUT response: status=${xhr.status} (${Date.now() - t0}ms total)`);
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else if (xhr.status === 403 && /AccessDenied/.test(xhr.responseText || '')) {
          // The presigned URL carries the SIGNER's permissions, so a 403 here
          // means the server's AWS identity lacks s3:PutObject on the bucket —
          // a deployment/IAM problem, not anything the user can retry away.
          const who = (xhr.responseText.match(/user\/([\w.+=,@-]+)/) || [])[1];
          reject(new Error('S3 rejected the upload: the server\'s AWS identity'
            + (who ? ` (${who})` : '') + ' is not allowed to write to the uploads bucket. '
            + 'This is a server permissions issue — retrying will not help.'));
        }
        else reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
      };
      xhr.onerror = (ev) => {
        // Browser fires onerror when CSP blocks, CORS preflight fails,
        // or the connection drops. The XHR has no detail — only the
        // browser's Network tab does.
        console.warn(`[large_file] PUT onerror at ${Date.now() - t0}ms — check Network tab for CSP/CORS/DNS issue`);
        reject(new Error('S3 PUT network error (browser blocked or connection lost — check DevTools Network tab)'));
      };
      xhr.ontimeout = () => {
        console.warn(`[large_file] PUT timed out at ${Date.now() - t0}ms`);
        reject(new Error('S3 PUT timed out after 30 min'));
      };
      xhr.onabort = () => {
        console.warn(`[large_file] PUT aborted at ${Date.now() - t0}ms`);
        reject(new Error('S3 PUT aborted'));
      };
      xhr.send(file);
    });

    // 3. Register — server HEADs the object to confirm it landed.
    // Strictly optional UX-wise (the agent could use s3Key without it),
    // but doing it catches "upload succeeded but bucket policy hid it"
    // cases before the agent ever tries.
    try {
      await fetch('/api/large-file/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        credentials: 'include',
        body: JSON.stringify({ s3Key }),
      });
    } catch (_) { /* non-fatal */ }

    console.log(`[large_file] uploadFileToS3 DONE in ${Date.now() - t0}ms`);
    return { s3Key };
  };

  /**
   * Kick off S3 uploads for any 'queued' entries and AWAIT all of them
   * before returning. Called from handleSubmit BEFORE we hit the agent
   * endpoint, so by the time the agent runs, every file is either
   * 'ready' (with an s3Key) or 'error'. Throws if any failed so the
   * caller can show the user.
   */
  const uploadQueuedFiles = async () => {
    const snapshot = largeFileUploads;
    const queued = snapshot.filter((u) => u.status === 'queued' && u._file);
    if (queued.length === 0) return { uploaded: 0, errors: [] };

    console.log(`[large_file] uploadQueuedFiles: starting ${queued.length} upload(s)`);
    // Mark all queued entries as 'uploading' atomically so the chip UI
    // flips to amber + progress bar right when send is clicked.
    setLargeFileUploads((prev) =>
      prev.map((u) => (u.status === 'queued' && u._file ? { ...u, status: 'uploading', progress: 0 } : u))
    );

    const results = await Promise.all(
      queued.map(async (entry) => {
        try {
          const { s3Key } = await uploadFileToS3(entry._file, (pct) => {
            setLargeFileUploads((prev) =>
              prev.map((u) => (u.tmpId === entry.tmpId ? { ...u, progress: pct } : u))
            );
          });
          // Update the ref SYNCHRONOUSLY so the in-flight handleSubmit
          // closure sees the new s3Key without waiting for a re-render.
          // This is what fixes the "1 upload total, 0 ready" stale-state
          // bug — state would only show 'ready' on the next render,
          // but our handler reads its value right now.
          largeFileReadyRef.current = [
            ...largeFileReadyRef.current,
            { tmpId: entry.tmpId, filename: entry.filename, size: entry.size, s3Key },
          ];
          setLargeFileUploads((prev) =>
            prev.map((u) =>
              u.tmpId === entry.tmpId ? { ...u, status: 'ready', progress: 100, s3Key, _file: null } : u
            )
          );
          return { tmpId: entry.tmpId, ok: true, s3Key };
        } catch (err) {
          console.warn(`[large_file] upload failed for ${entry.filename}:`, err);
          setLargeFileUploads((prev) =>
            prev.map((u) =>
              u.tmpId === entry.tmpId ? { ...u, status: 'error', error: err.message, _file: null } : u
            )
          );
          return { tmpId: entry.tmpId, ok: false, error: err.message, filename: entry.filename };
        }
      })
    );

    const errors = results.filter((r) => !r.ok);
    console.log(`[large_file] uploadQueuedFiles: ${results.length - errors.length} ok, ${errors.length} failed`);
    return { uploaded: results.length - errors.length, errors };
  };

  /* One ingestion path for every way a file can arrive — the picker or a drop
     on the page. Both must apply the same caps and the same large-file
     queueing, so this takes a plain list and the handlers below are thin
     wrappers over it. */
  const ingestFiles = (list) => {
    if (quickMode) return;
    const selected = Array.from(list || []);
    if (selected.length === 0) return;

    // Large File Agent branch: bypass Multer entirely.
    // - 5 GB cap (matches server's MAX_UPLOAD_BYTES).
    // - PDFs only — the agent's tools (pdf_info / pdf_search / etc.)
    //   wrap PyMuPDF, which is PDF-only.
    //
    // The file is ATTACHED to the queue with status 'queued' but NOT
    // uploaded yet. Upload kicks off when the user clicks send/analyze
    // (handleSubmit triggers uploadQueuedFiles). This matches the user-
    // intent flow: "I'm picking files for THIS analysis, not for an
    // arbitrary future use." Avoids burning S3 PUTs on files the user
    // attaches and then removes before sending.
    if (isLargeFileMode) {
      const maxSize = 5 * 1024 * 1024 * 1024; // 5 GB
      // Large File Agent stays PDF-only (its tools are PyMuPDF-centric).
      // RSI Agent + every user-created agent accept the multi-format set
      // (PDF, Office, text/data, archives, email, images, IFC, DWF) —
      // the server-side run_python_on_file + file_info + file_read tools
      // handle the rest.
      const RSI_ALLOWED_EXT = /\.(pdf|docx?|xlsx?|pptx?|csv|tsv|txt|md|html?|json|xml|ya?ml|rtf|eml|msg|zip|tar|gz|7z|png|jpe?g|gif|webp|bmp|tiff|svg|ifc|dwfx?)$/i;
      const allowFn = isWideFormatMode
        ? (f) => RSI_ALLOWED_EXT.test(f.name)
        : (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
      const valid = selected.filter((f) => f.size <= maxSize && allowFn(f));
      const dropped = selected.length - valid.length;
      if (dropped > 0) {
        const why = isWideFormatMode
          ? `${dropped} file(s) skipped (unsupported format or >5 GB). Supported: PDF, Office (docx/xlsx/pptx), CSV/TSV/TXT/MD, HTML/XML/JSON/YAML, RTF, eml/msg, zip/tar/gz/7z, images, IFC (BIM), DWF/DWFx (Autodesk).`
          : `${dropped} file(s) skipped (non-PDF or >5 GB)`;
        alert(why);
      }
      const entries = valid.map((f) => ({
        tmpId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        filename: f.name,
        size: f.size,
        // The actual File object is stashed in a ref (not state) so
        // it stays addressable when handleSubmit uploads. Putting File
        // objects in React state causes "non-serializable value" warnings
        // and unnecessary re-renders on every progress update.
        _file: f,
        status: 'queued',
        progress: 0,
      }));
      setLargeFileUploads((prev) => [...prev, ...entries]);
      return;
    }

    // Default path: file goes via Multer as part of the next agent
    // request. 500 MB cap to match Multer's existing limit.
    const maxSize = 500 * 1024 * 1024;
    const valid = selected.filter(f => f.size <= maxSize);
    if (valid.length < selected.length) alert(`${selected.length - valid.length} file(s) skipped (over 500 MB)`);
    setAttachedFiles(prev => [...prev, ...valid]);
  };

  const handleFileSelect = (e) => {
    ingestFiles(e.target.files);
    e.target.value = '';
  };

  const handleCompareFileSelect = (e) => {
    if (quickMode) {
      e.target.value = '';
      return;
    }
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    const maxSize = 500 * 1024 * 1024;
    const valid = selected.filter(f => f.size <= maxSize);
    if (valid.length < selected.length) alert(`${selected.length - valid.length} file(s) skipped (over 500 MB)`);
    setCompareFiles(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  useEffect(() => {
    quickModeRef.current = quickMode;
  }, [quickMode]);

  /* Drag and drop anywhere on the page. The browser's default is to NAVIGATE
     to a dropped file, silently destroying whatever the user had typed, so
     the listeners are unconditional; only the ingest is gated. dragenter and
     dragleave fire for every child element crossed, so the overlay is driven
     by a depth counter rather than by the events alone. */
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  useEffect(() => {
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const onEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      if (!quickModeRef.current) setDragActive(true);
    };
    const onOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
    const onLeave = (e) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragActive(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      const files = e.dataTransfer?.files;
      if (files && files.length) ingestFiles(files);
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [isLargeFileMode, isWideFormatMode]);

  useEffect(() => {
    if (quickMode) {
      setAttachedFiles([]);
      setCompareFiles([]);
      setCompareMode(false);
    }
  }, [quickMode]);

  useEffect(() => {
    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    axios.get('/api/feature-access/my-access')
      .then(res => { if (res.data.success) setDisabledTools(res.data.disabledTools || []); })
      .catch(() => {});
  }, []);

  const prevMessageCount = useRef(0);
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.type === 'bot') {
        setTimeout(() => latestBotRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
      prevMessageCount.current = messages.length;
    }
  }, [messages]);

  const prevStepCount = useRef(0);
  useEffect(() => {
    if (currentSteps.length > 0 && prevStepCount.current === 0) {
      setTimeout(() => liveStepsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
    prevStepCount.current = currentSteps.length;
  }, [currentSteps]);

  useEffect(() => {
    if (streamPreview.trim().length > 0) {
      setTimeout(() => liveStepsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);
    }
  }, [streamPreview]);

  useEffect(() => {
    const el = streamPreviewScrollRef.current;
    if (el && streamPreview.trim().length > 0) {
      el.scrollTop = el.scrollHeight;
    }
  }, [streamPreview]);

  // Stop any in-flight TTS on unmount (read-aloud buttons / voice-mode overlay).
  useEffect(() => () => stopText(), []);

  const requestPlan = async (q) => {
    const query = q || input;
    if (!query.trim() || isRunning || isPlanLoading) return;

    savedQuestionRef.current = query;
    setIsPlanLoading(true);
    setPlan(null);
    setEditedParams({});
    setCurrentSteps([]);
    setInput('');

    setMessages(prev => [...prev, { id: Date.now(), type: 'user', content: query, timestamp: new Date().toISOString() }]);

    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const res = await axios.post(
        '/api/jobotics-agent/plan',
        { question: query, loginEmail: user?.email || '', agentId },
        auth
      );
      const planData = res.data;

      if (planData.tool_name === 'send_email' && user?.email) {
        planData.params = {
          ...planData.params,
          to: coerceJoboticsSendEmailTo(planData.params?.to, user.email, query),
        };
      }

      const fileSuppliedTools = ['analyze_contract_text', 'generate_material_matrix'];
      const skipForm = attachedFiles.length > 0 && fileSuppliedTools.includes(planData.tool_name);

      if (!skipForm && planData.confirmable && planData.tool_name !== 'free_query') {
        setPlan(planData);
        setEditedParams(planData.params || {});
      } else {
        executeAgent(query, skipForm ? planData.tool_name : undefined, skipForm ? (planData.params || {}) : undefined);
      }
    } catch (err) {
      executeAgent(query);
    } finally {
      setIsPlanLoading(false);
    }
  };

  const executeAgent = async (q, selectedTool, toolParams, prevResult) => {
    const query = q || savedQuestionRef.current || input;
    if (!query.trim()) return;

    setPlan(null);
    setIsRunning(true);
    setCurrentSteps([]);
    setStreamPreview('');
    setProfileDecision(null);
    setPendingPause(null);
    setFollowUpSuggestions([]);

    const priorTurns =
      messages.length > 0 && messages[messages.length - 1]?.type === 'user'
        ? messages.slice(0, -1)
        : messages;
    const MAX_THREAD_MESSAGES = 24;
    const threadSlice = priorTurns
      .filter((m) => m.type === 'user' || m.type === 'bot')
      .slice(-MAX_THREAD_MESSAGES);
    const contextToSend = prevResult || (threadSlice.length > 0
      ? threadSlice
        .map((m) =>
          m.type === 'user'
            ? `User:\n${m.content}`
            : `Assistant:\n${m.content}`
        )
        .join('\n\n---\n\n')
      : undefined);

    // For the Large File Agent, prepend the ready S3 uploads' keys so
    // the model can use them as inputs to pdf_info / pdf_search /
    // pdf_read_pages / pdf_ocr_pages.
    //
    // Read from the REF, not state. React state updates from inside
    // uploadQueuedFiles don't propagate into this in-flight async
    // closure — the state value here would be stale (pre-upload).
    // The ref is updated synchronously on upload success, so it's
    // always current.
    let effectiveQuery = query;
    if (isLargeFileMode) {
      const ready = largeFileReadyRef.current;
      console.log(`[large_file] executeAgent: ${ready.length} ready file(s) in ref`);
      if (ready.length > 0) {
        const fileList = ready
          .map((u) => `- ${u.filename} (s3Key: ${u.s3Key}, ${(u.size / (1024 * 1024)).toFixed(1)} MB)`)
          .join('\n');
        // Stronger wording: "MUST pass exactly this s3Key string" so
        // the model doesn't strip the s3Key argument when calling pdf_*
        // tools. Previous phrasing left room for the model to forget.
        effectiveQuery =
          `ATTACHED FILES — you MUST pass the exact s3Key string below as the "s3Key" argument when calling pdf_info, pdf_search, pdf_read_pages, or pdf_ocr_pages. Do NOT call those tools without an s3Key argument.\n\n` +
          `${fileList}\n\n` +
          `USER REQUEST:\n${query}`;
        console.log(`[large_file] prepended ${ready.length} s3Key(s) to question — total ${effectiveQuery.length} chars`);
      } else {
        console.warn(`[large_file] agentId=large_file but ref is empty — uploads may have failed`);
      }
    }

    try {
      const auth = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const formData = new FormData();
      formData.append('question', effectiveQuery);
      if (user?.email) formData.append('loginEmail', user.email);
      if (agentId) formData.append('agentId', agentId);
      if (selectedTool && toolParams) {
        formData.append('selectedTool', selectedTool);
        formData.append('toolParams', JSON.stringify(toolParams));
      }
      if (contextToSend) {
        formData.append('previousResult', contextToSend);
      }
      if (!quickModeRef.current) {
        for (const f of attachedFiles) {
          formData.append('files', f);
        }
        for (const f of compareFiles) {
          formData.append('compareFiles', f);
        }
      }
      const endpoint = quickModeRef.current ? '/api/jobotics-agent/quick' : '/api/jobotics-agent/execute';
      // AbortController lets the "Stop the whole run" button (see
      // PauseModal + the always-visible stop button at the bottom
      // of the chat) actually cancel the in-flight stream. Without
      // this, a stuck run would just hang the UI.
      sseControllerRef.current = new AbortController();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { ...auth },
        credentials: 'include',
        body: formData,
        signal: sseControllerRef.current.signal,
      });

      const reader = response.body.getReader();
      // Capture the server's runId (sent as the first SSE event) so we
      // can attach it to the final message and offer "View step trace"
      // links even after a refresh.
      let currentRunId = null;
      const decoder = new TextDecoder();
      let buffer = '';
      let finalSteps = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = null;
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7);
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              if (eventType === 'run_start') {
                // Server announces the runId — stash it so we can
                // attach it to the final message and surface "View
                // step trace" links to the user.
                if (data.runId) currentRunId = data.runId;
              } else if (eventType === 'profile_decision') {
                // Trust Layer Phase B: the agent's profile classifier
                // returned a decision. Show it in a banner above the
                // activity stream so the user can see what the agent
                // plans to do (and abort if it picked wrong).
                setProfileDecision(data);
              } else if (eventType === 'pause_request') {
                // Trust Layer Phase C + true gates: agent called
                // request_user_input and is paused server-side waiting
                // for a choice. Pop the modal. The agent loop is
                // suspended until the user POSTs to /resume.
                setPendingPause(data);
              } else if (eventType === 'step') {
                // Inject runId + agentId so corrections POSTs can
                // attribute each edit to the right run/agent without
                // the StepItem needing access to the outer scope.
                const enriched = { ...data, _runId: currentRunId, _agentId: agentId || null };
                setCurrentSteps(prev => {
                  const idx = prev.findIndex(s => s.id === enriched.id);
                  let updated;
                  if (idx >= 0) {
                    updated = [...prev];
                    updated[idx] = { ...updated[idx], ...enriched };
                  } else {
                    if (enriched.status === 'running') enriched.startedAt = Date.now();
                    updated = [...prev, enriched];
                  }
                  finalSteps = updated;
                  return updated;
                });
              } else if (eventType === 'partial') {
                if (data.clear) setStreamPreview('');
                else if (typeof data.text === 'string') setStreamPreview(data.text);
              } else if (eventType === 'tool_progress') {
                // Live progress from long-running tools (run_python_extract /
                // run_python_on_file). The runner emits these every 500 rows
                // or 3 seconds. Surface as a detail on the currently-running
                // step so the user sees "5,400 rows / 67s" instead of a
                // silent spinner.
                setCurrentSteps(prev => {
                  if (prev.length === 0) return prev;
                  const last = prev[prev.length - 1];
                  if (last.status !== 'running') return prev;
                  const parts = [];
                  if (data.rowsEmitted != null) parts.push(`${data.rowsEmitted.toLocaleString()} rows`);
                  if (data.pagesProcessed != null && data.pagesProcessed > 0) parts.push(`p${data.pagesProcessed}`);
                  if (data.elapsedSec != null) parts.push(`${data.elapsedSec.toFixed(1)}s`);
                  const detail = parts.join(' · ');
                  return [...prev.slice(0, -1), { ...last, detail, progress: { ...data } }];
                });
              } else if (eventType === 'result') {
                const doneSteps = finalSteps.map(s => ({ ...s, status: 'done' }));
                const displayText = scrubResultEmailText(data.text, user?.email);
                void persistJoboticsTurn({
                  query,
                  assistantText: data.text,
                  steps: doneSteps,
                  quick: quickModeRef.current,
                  via: 'text',
                  agentId: agentId || null,
                });
                setMessages(prev => [...prev, {
                  id: Date.now(),
                  type: 'bot',
                  content: displayText,
                  steps: doneSteps,
                  runId: currentRunId,
                  timestamp: new Date().toISOString()
                }]);
                setCurrentSteps([]);
                setStreamPreview('');
                setFollowUpSuggestions(generateFollowUps(query, doneSteps, attachedFiles.length > 0));
              } else if (eventType === 'error') {
                setMessages(prev => [...prev, {
                  id: Date.now(),
                  type: 'bot',
                  content: data.message,
                  error: true,
                  steps: finalSteps.map(s => ({ ...s, status: 'done' })),
                  runId: currentRunId,
                  timestamp: new Date().toISOString()
                }]);
                setCurrentSteps([]);
                setStreamPreview('');
              } else if (eventType === 'done') {
                setCurrentSteps(prev => prev.map(s => s.status === 'running' ? { ...s, status: 'done' } : s));
              }
            } catch {}
            eventType = null;
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content: err.message,
        error: true,
        timestamp: new Date().toISOString()
      }]);
      setCurrentSteps([]);
      setStreamPreview('');
    } finally {
      setIsRunning(false);
    }
  };

  const generateFollowUps = (query, toolsUsed, hasUploadedFiles) => {
    const q = query.toLowerCase();
    const tools = (toolsUsed || []).map(s => s.tool).filter(Boolean);
    const suggestions = [];

    if (hasUploadedFiles && !tools.includes('generate_material_matrix') && !q.includes('material matrix') && !q.includes('bom')) {
      suggestions.push('Convert the uploaded file to a material matrix / BOM');
    }

    const hasLabor =
      tools.includes('batch_estimate_labor_cost') ||
      tools.includes('estimate_labor_cost') ||
      tools.includes('batch_estimate_neca_labor_cost') ||
      tools.includes('estimate_neca_labor_cost') ||
      q.includes('labor');
    const hasBOM = tools.includes('generate_material_matrix') || q.includes('bom') || q.includes('bill of material') || q.includes('material matrix') || q.includes('takeoff');
    const hasSlides = tools.includes('generate_slides');
    const hasExcel = tools.includes('generate_excel');
    const hasWord = tools.includes('generate_word');
    const hasPdf = tools.includes('generate_pdf');
    const hasDash = tools.includes('generate_dashboard');
    const hasEmail = tools.includes('send_email');

    if (hasLabor) {
      suggestions.push('Export the labor estimate to Excel');
      suggestions.push('What are the total labor hours for the full line list?');
    }
    if (hasBOM) {
      suggestions.push('Estimate labor hours in batch for these line items');
      suggestions.push('Email me the BOM spreadsheet');
    }
    if (hasSlides && !hasDash) {
      suggestions.push('Build an HTML dashboard with the same story and key numbers');
    }
    if (hasDash && !hasSlides) {
      suggestions.push('Create a short executive slide deck from this dashboard');
    }
    if (hasExcel && !hasPdf) {
      suggestions.push('Generate a one-page PDF handout from this workbook');
    }
    if ((hasWord || hasPdf) && !hasExcel) {
      suggestions.push('Put the same content into a formatted Excel table');
    }
    if (hasEmail) {
      suggestions.push('Send a shorter follow-up to the same thread with bullet action items');
    }

    const hasWebSearch = tools.includes('web_search') || q.includes('competitor') || q.includes('market') || q.includes('research') || q.includes('image');
    if (hasWebSearch) {
      suggestions.push('Compile the research into a comparison table in Excel');
      suggestions.push('Find reference or product images on the web related to this topic');
    }

    if (suggestions.length === 0) {
      suggestions.push(
        'Look up labor hours for a described fitting or fixture',
        'Build a material matrix from uploaded PDF drawings',
        'Research a construction topic on the web and summarize with sources'
      );
    }

    const unique = [...new Set(suggestions)];
    const filtered = unique.filter((s) => s.toLowerCase() !== q);

    // When on a specialized agent page, rank the agent's MEP-domain suggested
    // queries by keyword overlap with the user's last question and prepend the
    // top matches. This makes follow-ups feel related to what the user just
    // asked instead of generic Jobotics Agent suggestions.
    if (agentId && Array.isArray(agentSuggestedQueries) && agentSuggestedQueries.length) {
      const queryTokens = new Set((q.match(/[a-z0-9]{4,}/g) || []));
      const score = (candidate) => {
        const cTokens = (candidate.toLowerCase().match(/[a-z0-9]{4,}/g) || []);
        let n = 0;
        for (const t of cTokens) if (queryTokens.has(t)) n += 1;
        return n;
      };
      const ranked = [...agentSuggestedQueries]
        .filter((s) => s.toLowerCase() !== q)
        .map((s) => ({ s, score: score(s) }))
        // Stable sort: higher overlap first; ties preserve original order.
        .sort((a, b) => b.score - a.score)
        .map((x) => x.s);
      const merged = [...new Set([...ranked, ...filtered])];
      return merged.slice(0, 4);
    }

    return filtered.slice(0, 4);
  };

  const handleConfirm = () => {
    const cleanParams = {};
    for (const [k, v] of Object.entries(editedParams)) {
      if (v !== '' && v !== undefined && v !== null) cleanParams[k] = v;
    }
    executeAgent(savedQuestionRef.current, plan.tool_name, cleanParams);
  };

  const handleSkip = () => {
    executeAgent(savedQuestionRef.current);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q || busy) return;
    // Large File Agent flow:
    //   1. If any files are 'queued' (just attached, not yet uploaded),
    //      upload them now and AWAIT completion before proceeding. The
    //      chip flips amber → emerald as uploads finish.
    //   2. If any uploads are mid-flight (status: 'uploading') AT THE
    //      MOMENT send is clicked, that means a previous send already
    //      kicked off uploads — block re-submit until they finish.
    //   3. If everything failed, surface a clear error.
    if (isLargeFileMode) {
      const queued = largeFileUploads.filter((u) => u.status === 'queued');
      const stillUploading = largeFileUploads.filter((u) => u.status === 'uploading');
      if (stillUploading.length > 0) {
        alert(
          `${stillUploading.length} file(s) still uploading to S3 — please wait a moment.`
        );
        return;
      }
      if (queued.length > 0) {
        // Trigger uploads now. busy stays false so the UI knows we're
        // in the "uploading attachments" phase, not the "running agent"
        // phase. The chip row shows progress.
        const { errors } = await uploadQueuedFiles();
        if (errors.length > 0) {
          alert(
            `${errors.length} file(s) failed to upload:\n` +
              errors.map((e) => `• ${e.filename}: ${e.error}`).join('\n') +
              `\n\nFix and try again — only successful uploads will be sent to the agent.`
          );
          // Allow proceeding ONLY if at least one upload succeeded; otherwise bail.
          const ready = largeFileUploads.filter((u) => u.status === 'ready');
          if (ready.length === 0) return;
        }
      }
      const anyErrored = largeFileUploads.filter((u) => u.status === 'error');
      const anyReady = largeFileUploads.filter((u) => u.status === 'ready');
      if (anyErrored.length > 0 && anyReady.length === 0) {
        alert(`All uploads failed. Remove the red chip(s) and re-attach.`);
        return;
      }
    }
    if (quickMode) {
      savedQuestionRef.current = q;
      setInput('');
      setPlan(null);
      setEditedParams({});
      setCurrentSteps([]);
      setMessages((prev) => [...prev, { id: Date.now(), type: 'user', content: q, timestamp: new Date().toISOString() }]);
      executeAgent(q);
      return;
    }
    requestPlan(q);
  };

  const applySampleQuery = (text) => {
    setInput(text);
    setTimeout(() => (heroInputRef.current || inputRef.current)?.focus(), 0);
  };

  const handleDownloadFile = async (filename) => {
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const res = await axios.get(`/api/project-ops/download-sharepoint?filename=${encodeURIComponent(filename)}`, {
        ...auth,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Download failed: ${err.response?.data?.error || err.message}. You may need to re-authenticate with SharePoint.`);
    }
  };

  const busy = isRunning || isPlanLoading;
  const showHeroComposer = messages.length === 0 && !busy && !plan;

  const mdComponents = {
    table: ({ children }) => (
      <div className="overflow-x-auto my-3">
        <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    th: ({ children }) => <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-b border-gray-200">{children}</th>,
    td: ({ children }) => <td className="px-3 py-2 text-gray-700 border-b border-gray-100">{children}</td>,
    h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
    h2: ({ children }) => <h2 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h2>,
    h3: ({ children }) => <h3 className="text-base font-semibold text-gray-800 mt-3 mb-1">{children}</h3>,
    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
    ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
    li: ({ children }) => <li className="text-gray-700">{children}</li>,
    p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
    a: ({ href, children }) => {
      if (!href || href === '#') return <span className="text-indigo-600">{children}</span>;
      const isDownloadSP = href.includes('/api/project-ops/download-sharepoint');
      const isAgentDownload = href.includes('/api/jobotics-agent/download/');
      const isAgentView = href.includes('/api/jobotics-agent/view/');
      if (isDownloadSP) {
        return (
          <button
            onClick={() => handleDownloadFile(decodeURIComponent(href.split('filename=')[1] || ''))}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors cursor-pointer"
          >
            <FileText className="w-3.5 h-3.5" />
            {children}
          </button>
        );
      }
      if (isAgentDownload) {
        return (
          <a
            href={href}
            download
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors cursor-pointer no-underline"
          >
            <FileText className="w-3.5 h-3.5" />
            {children}
          </a>
        );
      }
      if (isAgentView) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors cursor-pointer no-underline"
          >
            <Presentation className="w-3.5 h-3.5" />
            {children}
          </a>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">{children}</a>;
    },
    code: ({ inline, children }) => inline
      ? <code className="px-1.5 py-0.5 bg-gray-100 text-indigo-700 rounded text-xs font-mono">{children}</code>
      : <pre className="bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-xs font-mono my-2"><code>{children}</code></pre>,
  };

  // Bare /jobotics-agent (no agentId) routes back to Home so users don't see
  // the generic agent without a persona. /jobotics-agent?agentId=<slug> stays.
  if (shouldRedirectToHome) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="h-full flex flex-col min-h-0 relative">
      {/* Drop overlay — pointer-events-none so it never eats the drop itself;
          the window listener does the work. */}
      {dragActive && (
        <div className="fixed inset-0 z-[80] pointer-events-none flex items-center justify-center
                        bg-indigo-950/40 backdrop-blur-[2px]">
          <div className="rounded-2xl border-2 border-dashed border-indigo-300 bg-white/95
                          px-10 py-8 text-center shadow-2xl">
            <Upload className="w-8 h-8 mx-auto text-indigo-500" />
            <p className="mt-3 text-lg font-semibold text-gray-900">Drop to attach</p>
            <p className="mt-1 text-sm text-gray-500">
              {isLargeFileMode
                ? (isWideFormatMode
                    ? 'PDF, Office, text/data, archives, images, IFC, DWF — up to 5 GB each'
                    : 'PDF only — up to 5 GB each')
                : 'Up to 500 MB each'}
            </p>
          </div>
        </div>
      )}
      {historyOpen && (
        <>
          <button
            type="button"
            aria-label="Close history"
            className="fixed inset-0 z-[55] bg-black/25"
            onClick={() => setHistoryOpen(false)}
          />
          <aside className="fixed inset-y-0 right-0 z-[60] w-full max-w-sm bg-white border-l border-gray-200 shadow-xl flex flex-col">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Jobotics history</h2>
                <p className="text-[11px] text-gray-500 mt-0.5">Same storage as Chat History (filtered)</p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {historyLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : historySessions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10 px-3">
                  No saved Jobotics threads yet. After you get a reply, it appears here automatically.
                </p>
              ) : (
                <ul className="space-y-1">
                  {historySessions.map((session) => (
                    <li key={session.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => loadHistorySessionMessages(session.id)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            loadHistorySessionMessages(session.id);
                          }
                        }}
                        className={`w-full text-left rounded-lg p-3 cursor-pointer border transition-colors group ${
                          activeHistoryPrefixedId === session.id
                            ? 'bg-indigo-50 border-indigo-200'
                            : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{session.title || 'Jobotics chat'}</p>
                            <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3 shrink-0" />
                              {formatHistoryTime(session.updated_at)}
                              {session.message_count != null && (
                                <span className="text-gray-400"> · {session.message_count} msgs</span>
                              )}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => deleteJoboticsHistorySession(session.id, e)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all shrink-0"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <Link
                to="/user-chat"
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 underline"
                onClick={() => setHistoryOpen(false)}
              >
                Open Chat History (demo: Jobotics, PM Voice, Contract Redline…)
              </Link>
            </div>
          </aside>
        </>
      )}
      <div className="flex-1 flex flex-col gap-3 min-h-0">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 shrink-0">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
                {/* Use the agent's emoji on persona pages so the badge feels
                    specific; fall back to the generic Bot icon for the bare
                    /jobotics-agent surface. */}
                {agentMeta ? (
                  <span className="text-2xl leading-none" aria-hidden>{agentMeta.emoji}</span>
                ) : (
                  <Bot className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900">
                  {agentMeta ? agentMeta.name : 'Jobotics Agent'}
                </h1>
                {quickMode ? (
                  <p className="text-sm text-gray-500">
                    Quick mode — MCAA / Weblem, NECA labor, web search, and email; skips plan; attachments off.
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">
                    {agentMeta
                      ? agentMeta.blurb
                      : 'A more comprehensive AEC agent for research, documents, labor, and deliverables — general-purpose assistant.'}
                  </p>
                )}
              </div>
            </div>
            {/* Top-right header actions — Stats + Pin on persona pages. */}
            {agentMeta && (
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate(`/agents/${encodeURIComponent(agentMeta.id)}/stats`)}
                  title="See this agent's run history + confidence distribution"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border bg-white border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <BarChart3 size={14} />
                  <span>Stats</span>
                </button>
                <button
                  type="button"
                  onClick={() => togglePin(agentMeta.id)}
                  title={currentPinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
                  aria-pressed={currentPinned}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    currentPinned
                      ? 'bg-sylvan-50 border-sylvan-200 text-sylvan-700 hover:bg-sylvan-100'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {currentPinned ? <PinOff size={14} /> : <Pin size={14} className="rotate-45" />}
                  <span>{currentPinned ? 'Pinned' : 'Pin'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Chat Container */}
        <div className="w-full flex-1 min-h-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-200px)] min-h-[320px] flex flex-col">
            {/* The RSI Agent supports a wider multi-format set than the other
                agents (via server-side markitdown + file_python_runner).
                Other agents keep the narrower historical list — they were
                not designed for HTML/eml/archive inputs. */}
            <input ref={fileInputRef} type="file" className="hidden"
                   accept={isWideFormatMode
                     ? '.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.csv,.tsv,.txt,.md,.html,.htm,.json,.xml,.yaml,.yml,.rtf,.eml,.msg,.zip,.tar,.gz,.7z,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff,.svg,.ifc,.dwf,.dwfx'
                     : '.pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.json'}
                   multiple onChange={handleFileSelect} />
            <input ref={compareFileInputRef} type="file" className="hidden"
                   accept={isWideFormatMode
                     ? '.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.csv,.tsv,.txt,.md,.html,.htm,.json,.xml,.yaml,.yml,.rtf,.eml,.msg,.zip,.tar,.gz,.7z,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff,.svg,.ifc,.dwf,.dwfx'
                     : '.pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.json'}
                   multiple onChange={handleCompareFileSelect} />
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-8 min-h-0">

              {showHeroComposer && (
                <div className="flex flex-col items-center justify-center py-6 px-2 min-h-[min(60vh,520px)]">
                  <div className="text-center mb-8 max-w-xl mx-auto space-y-2">
                    {agentMeta ? (
                      <>
                        <div className="flex items-center justify-center gap-2 text-sm text-indigo-600 font-medium">
                          <span aria-hidden className="text-xl">{agentMeta.emoji}</span>
                          <span>{agentMeta.name}</span>
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-serif text-gray-900 leading-snug">
                          How can I help with {agentMeta.name.replace(/ Agent$/, '').toLowerCase()}?
                        </h2>
                        {/* Blurb intentionally NOT rendered here — it becomes the
                            chat-input placeholder below so the header stays clean. */}
                      </>
                    ) : (
                      <>
                        <h2 className="text-2xl sm:text-3xl font-serif text-gray-900 leading-snug">
                          What can we help you with?
                        </h2>
                        {!quickMode && (
                          <p className="text-sm text-gray-500">
                            Synthetic demo project data — financial, ops, or strategy.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="w-full max-w-2xl">
                    {!quickMode && attachedFiles.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2 justify-center">
                        {attachedFiles.map((f, i) => (
                          <div key={i} className="flex items-center gap-1.5 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded-lg text-xs">
                            <FileText className="w-3 h-3 text-indigo-600" />
                            <span className="text-indigo-700 truncate max-w-[180px]">{f.name}</span>
                            <button type="button" onClick={() => setAttachedFiles((prev) => prev.filter((_, j) => j !== i))} className="text-indigo-400 hover:text-indigo-700">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Large File uploads — staged direct-to-S3 with live
                        progress. See LargeFileUploadChips for the four
                        visual states. */}
                    {isLargeFileMode && (
                      <div className="flex justify-center">
                        <LargeFileUploadChips
                          uploads={largeFileUploads}
                          onRemove={(tmpId) => {
                            largeFileReadyRef.current = largeFileReadyRef.current.filter((r) => r.tmpId !== tmpId);
                            setLargeFileUploads((prev) => prev.filter((p) => p.tmpId !== tmpId));
                          }}
                        />
                      </div>
                    )}
                    <form
                      onSubmit={handleSubmit}
                      className="rounded-2xl border border-gray-200 bg-white shadow-sm p-3 transition-shadow focus-within:shadow-md focus-within:border-indigo-200 focus-within:ring-2 focus-within:ring-indigo-500/15"
                    >
                      <textarea
                        ref={heroInputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        rows={3}
                        placeholder={
                          // Agent personas: surface the per-agent blurb as the
                          // placeholder so the header stays clean and the input
                          // doubles as a contextual hint. Falls back to the
                          // generic placeholder when no agent is active.
                          agentMeta
                            ? agentMeta.blurb
                            : quickMode
                              ? 'MCAA / Weblem, NECA, batch labor (paste lines), or web research…'
                              : 'Ask any project question — financial, ops, strategy… (demo dataset + email/slides as usual)'
                        }
                        disabled={busy}
                        className="w-full min-h-[5.5rem] resize-none border-0 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:ring-0 disabled:opacity-50"
                      />
                      <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          {!quickMode && (
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={busy}
                              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-500 hover:text-indigo-600 hover:border-indigo-200 disabled:opacity-40"
                              title="Attach file(s)"
                            >
                              <Paperclip className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setQuickMode((v) => !v)}
                            disabled={busy}
                            title={
                              quickMode
                                ? 'Quick on — MCAA / Weblem, NECA, web search; skips plan; no uploads'
                                : 'Turn on quick mode'
                            }
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-40 ${
                              quickMode
                                ? 'bg-amber-50 border-amber-300 text-amber-900'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-amber-200 hover:text-amber-900'
                            }`}
                          >
                            <Zap className="w-3.5 h-3.5" />
                            Quick
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Voice input — promoted to equal weight with Send so users can
                              pick between text and voice with equal friction. Live transcript
                              streams into the input as you speak (Web Speech API). */}
                          {/* Mic = dictation only: streams transcript into the text bar.
                              Voice MODE is a separate full-screen experience triggered
                              by the orb button below. The two are now deliberately
                              decoupled so users can dictate text without entering an
                              immersive voice conversation. */}
                          <VoiceMicButton
                            getCurrentText={() => input}
                            onUpdate={(text) => setInput(text)}
                            className="w-9 h-9 flex items-center justify-center rounded-full border border-indigo-200 bg-white hover:bg-indigo-50"
                          />
                          {/* Voice-mode orb: opens the immersive overlay. */}
                          <button
                            type="button"
                            onClick={() => setVoiceModeOpen(true)}
                            title="Open voice mode (bidirectional voice conversation)"
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                          >
                            <span className="flex items-end gap-[2px]" aria-hidden>
                              <span className="block w-[2px] h-2 bg-white rounded-full" />
                              <span className="block w-[2px] h-3 bg-white rounded-full" />
                              <span className="block w-[2px] h-4 bg-white rounded-full" />
                              <span className="block w-[2px] h-3 bg-white rounded-full" />
                              <span className="block w-[2px] h-2 bg-white rounded-full" />
                            </span>
                          </button>
                          <button
                          type="submit"
                          disabled={busy || !input.trim()}
                          className="w-9 h-9 flex items-center justify-center rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Send"
                        >
                          {isPlanLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                        </div>
                      </div>
                    </form>

                    {/* Agent-specific suggested queries (shown only when ?agentId=<slug>
                        matches a known persona; falls back to the generic chip row below). */}
                    {agentSuggestedQueries && (() => {
                      // Quick toggle ON  → render the agent's *quick* chips (atomic
                      //                    factoid lookups; runs through /quick path).
                      // Quick toggle OFF → render the agent's 4 default suggested
                      //                    queries (multi-step asks; full /execute path).
                      // Each agent has its own pair, so the chip set always feels
                      // specific to the persona — never the generic AEC chip row.
                      const chips =
                        quickMode && agentQuickQueries && agentQuickQueries.length
                          ? agentQuickQueries
                          : agentSuggestedQueries;
                      const label = quickMode
                        ? 'Quick mode — fast lookups for this agent:'
                        : 'Try one of these to get started:';
                      return (
                        <div className="mt-5">
                          <p className="text-xs text-gray-500 mb-2 text-center">{label}</p>
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            {chips.map((q, qi) => (
                              <button
                                key={qi}
                                type="button"
                                onClick={() => applySampleQuery(q)}
                                className={`px-3 py-1.5 text-xs leading-snug rounded-full max-w-full text-left ${
                                  quickMode
                                    ? 'bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100'
                                    : 'bg-white border border-indigo-200 text-indigo-800 hover:bg-indigo-50'
                                }`}
                              >
                                {q.length > 120 ? `${q.slice(0, 118)}…` : q}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Generic AEC chip row is ONLY for the generic Jobotics Agent
                        surface (no agentId). On specialized agent pages, the chip
                        row above is the *agent's* own list, swapped between
                        suggested-queries and quick-queries by the Quick toggle. */}
                    {!agentId && (
                    <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                      {(quickMode ? JOBOTICS_QUICK_MODE_TASK_CHIPS : QUICK_TASKS).map(({ id, label, Icon, sample }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => applySampleQuery(sample)}
                          title={sample.length > 80 ? sample : undefined}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-indigo-200 hover:text-indigo-800 transition-colors"
                        >
                          <Icon className="w-3.5 h-3.5 text-indigo-600" />
                          {label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setShowMoreExamples((v) => !v)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                      >
                        <MoreHorizontal className="w-3.5 h-3.5" />
                        {showMoreExamples ? 'Less' : 'More'}
                      </button>
                    </div>
                    )}

                    {!agentId && showMoreExamples && (
                      <div
                        className={`mt-4 rounded-xl border border-gray-100 bg-gray-50/80 p-3 overflow-y-auto ${
                          quickMode ? 'max-h-64' : 'max-h-96'
                        }`}
                      >
                        <div className="space-y-3">
                          {(quickMode ? JOBOTICS_QUICK_MODE_EXAMPLE_GROUPS : EXAMPLE_QUESTION_GROUPS).map((group, gi) => (
                            <div key={gi}>
                              {!quickMode && FIRST_AEC_EXAMPLE_GROUP_INDEX >= 0 && gi === FIRST_AEC_EXAMPLE_GROUP_INDEX && (
                                <div className="mb-3 pb-2 border-b border-indigo-100">
                                  <p className="text-xs font-semibold text-indigo-900">AEC industry data</p>
                                  <p className="text-[10px] text-gray-500 mt-0.5">
                                    Pick a use case below — you can follow up with a dashboard, deck, or email anytime.
                                  </p>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <span className="text-sm">{group.icon}</span>
                                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{group.label}</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {group.questions.map((q, qi) => (
                                  <button
                                    key={qi}
                                    type="button"
                                    onClick={() => applySampleQuery(q)}
                                    className="px-2.5 py-1 text-left text-[11px] leading-snug bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-800 transition-colors max-w-full"
                                  >
                                    {q.length > 120 ? `${q.slice(0, 118)}…` : q}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Threaded Messages */}
              {messages.map((message, msgIdx) => {
                const isLastBot = message.type === 'bot' && msgIdx === messages.length - 1;
                return (
                <div key={message.id} ref={isLastBot ? latestBotRef : undefined} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-4xl w-full rounded-lg px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-indigo-600 text-white ml-12'
                      : message.error
                      ? 'bg-red-50 text-red-800 border border-red-200 mr-12'
                      : 'bg-gray-50 text-gray-900 border border-gray-200 mr-12'
                  }`}>
                    <div className="flex items-start gap-2">
                      {message.type === 'bot' && <Bot className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />}
                      {message.type === 'user' && <Send className="w-4 h-4 text-white/70 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        {message.type === 'user' ? (
                          <p className="text-sm">{message.content}</p>
                        ) : (
                          <>
                            {/* Collapsible execution steps */}
                            {message.steps && message.steps.length > 0 && (
                              <details className="mb-3">
                                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 flex items-center gap-1">
                                  <Terminal className="w-3 h-3" />
                                  {message.steps.length} tool{message.steps.length > 1 ? 's' : ''} executed
                                  {message.runId && (
                                    <a
                                      href={`/runs/${message.runId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="ml-2 text-indigo-600 hover:text-indigo-800 underline"
                                    >view full log</a>
                                  )}
                                </summary>
                                <div className="mt-2 space-y-1 pl-4 border-l-2 border-gray-200">
                                  {message.steps.map((step) => {
                                    const Icon = step.tool ? (TOOL_ICONS[step.tool] || Database) : Cpu;
                                    return (
                                      <div key={step.id} className="flex items-center gap-2 text-xs text-gray-600">
                                        <Icon className="w-3 h-3 flex-shrink-0" />
                                        <span>{step.label}</span>
                                        {step.duration && <span className="text-gray-400">{step.duration}</span>}
                                        {step.rows?.length > 0 && (
                                          <span className="text-gray-400">{step.rows.length} rows</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </details>
                            )}
                            <div className="prose prose-sm max-w-none text-gray-800">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{message.content}</ReactMarkdown>
                            </div>
                          </>
                        )}
                      </div>
                      {/* Read-aloud button: top-right of assistant bubble. Uses a
                          getter so the latest content is read (works while still
                          streaming if the user clicks before completion). */}
                      {message.type === 'bot' && !message.error && message.content && (
                        <SpeakButton
                          getText={() => message.content}
                          className="p-1.5 rounded-md hover:bg-gray-100 flex-shrink-0 self-start"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )})}

              {/* Trust Layer Phase C + true gates: pause-and-ask modal.
                  Renders inline above the activity stream rather than
                  as an overlay so it doesn't fight the chat layout.
                  Agent loop is suspended server-side until user picks. */}
              {pendingPause && (
                <PauseModal
                  pause={pendingPause}
                  submitting={pauseSubmitting}
                  onCancelRun={() => {
                    // Stop-run: close the SSE source and reset the
                    // running flag. Server's pause auto-cancels after
                    // 10 min; we cleanly resolve it now with 'cancel'
                    // so the agent loop returns immediately and the
                    // run record gets a clean 'cancelled' status.
                    if (pendingPause?.pauseId) {
                      fetch('/api/jobotics-agent/resume', {
                        method: 'POST', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ pauseId: pendingPause.pauseId, choice: 'cancel' }),
                      }).catch(() => {});
                    }
                    if (sseControllerRef.current) {
                      try { sseControllerRef.current.abort(); } catch (_) {}
                    }
                    setIsRunning(false);
                    setPendingPause(null);
                  }}
                  onSubmit={async (choice, notes, extra) => {
                    setPauseSubmitting(true);
                    try {
                      const r = await fetch('/api/jobotics-agent/resume', {
                        method: 'POST', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          pauseId: pendingPause.pauseId,
                          choice,
                          notes: notes || null,
                          // For text / file / other rich answers, the
                          // tool result includes these payload fields
                          // so the agent's persona can consume them.
                          ...(extra || {}),
                        }),
                      });
                      if (!r.ok) {
                        const body = await r.json().catch(() => ({}));
                        const errCode = (body.error || '').toString();
                        // The pause is gone — server restarted (most common
                        // on Railway after a deploy) or pause expired.
                        // Don't strand the user in modal+running state.
                        // Auto-recover: clean up state and pre-fill the
                        // composer with their chosen option + notes so
                        // they can resend as a fresh follow-up.
                        if (r.status === 404 || errCode === 'not_found_or_expired') {
                          const chosenLabel = (pendingPause.options || [])
                            .find((o) => o.value === choice)?.label || choice;
                          const followup = choice === 'text' && extra?.text
                            ? `${extra.text}${notes ? `\n\nWhy: ${notes}` : ''}`
                            : `${chosenLabel}${notes ? `\n\nWhy: ${notes}` : ''}`;
                          if (sseControllerRef.current) {
                            try { sseControllerRef.current.abort(); } catch (_) {}
                          }
                          setIsRunning(false);
                          setPendingPause(null);
                          setInput(followup);
                          alert(
                            'The agent\'s question expired (this usually happens when the server has just restarted). ' +
                            'Your answer has been put into the message box — review and click Send to continue.'
                          );
                          return;
                        }
                        throw new Error(errCode || `HTTP ${r.status}`);
                      }
                      setPendingPause(null);
                    } catch (e) {
                      alert(`Could not submit your choice: ${e.message}`);
                    } finally {
                      setPauseSubmitting(false);
                    }
                  }}
                />
              )}

              {/* Trust Layer Phase B: the agent's profile decision —
                  what it classified the upload as, what strategy it
                  picked, expected row count, cache hit. Visible above
                  the activity stream so the user can see whether the
                  agent's plan matches what they wanted. */}
              {profileDecision && (
                <div className="flex justify-start">
                  <div className="max-w-4xl w-full bg-indigo-50/60 border border-indigo-200 rounded-lg px-4 py-3 mr-12">
                    <div className="flex items-baseline gap-2 mb-1">
                      <ShieldCheck className="w-4 h-4 text-indigo-600 self-center" />
                      <span className="text-sm font-semibold text-indigo-900">Agent's plan</span>
                      {profileDecision.cached && (
                        <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-1.5 py-0.5">
                          Cache hit
                        </span>
                      )}
                      <span className="ml-auto text-xs text-indigo-700/70">{profileDecision.tool}</span>
                    </div>
                    <div className="text-xs text-slate-800 space-y-0.5 ml-6">
                      {profileDecision.content_class && (
                        <div><span className="text-slate-500">Detected:</span> <strong>{profileDecision.content_class}</strong>{profileDecision.structure_type ? ` · ${profileDecision.structure_type}` : ''}</div>
                      )}
                      {profileDecision.type && (
                        <div><span className="text-slate-500">File:</span> {profileDecision.type}{profileDecision.ext ? ` (${profileDecision.ext})` : ''}{profileDecision.size ? ` · ${(profileDecision.size / 1024 / 1024).toFixed(1)} MB` : ''}</div>
                      )}
                      {profileDecision.page_count != null && (
                        <div><span className="text-slate-500">Pages:</span> {profileDecision.page_count.toLocaleString()}</div>
                      )}
                      {profileDecision.sheetCount != null && (
                        <div><span className="text-slate-500">Sheets:</span> {profileDecision.sheetCount} ({(profileDecision.sheetNames || []).slice(0,3).join(', ')}{(profileDecision.sheetNames || []).length > 3 ? '…' : ''})</div>
                      )}
                      {profileDecision.slideCount != null && (
                        <div><span className="text-slate-500">Slides:</span> {profileDecision.slideCount}</div>
                      )}
                      {profileDecision.expected_rows_estimate != null && (
                        <div><span className="text-slate-500">Expected rows:</span> ~{Number(profileDecision.expected_rows_estimate).toLocaleString()}</div>
                      )}
                      {(profileDecision.working_strategy || profileDecision.recommended_strategy) && (
                        <div><span className="text-slate-500">Plan:</span> <code className="text-[11px] bg-white border border-indigo-100 rounded px-1 py-0.5">{profileDecision.working_strategy || profileDecision.recommended_strategy}</code></div>
                      )}
                      {profileDecision.reasoning && (
                        <div className="text-slate-600 mt-1 italic">{profileDecision.reasoning}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Live execution steps + streamed assistant text (while running) */}
              {(currentSteps.length > 0 || streamPreview.trim().length > 0) && (
                <div ref={liveStepsRef} className="flex justify-start">
                  <div className="max-w-4xl w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mr-12">
                    <div className="flex items-center gap-2 mb-3">
                      <Bot className="w-5 h-5 text-indigo-600" />
                      <Terminal className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">
                        {quickMode ? 'Quick mode…' : 'Agent working...'}
                      </span>
                      <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                      {/* Universal escape hatch — visible whenever the
                          agent is mid-run, regardless of pause state.
                          Aborts the streaming fetch, resolves any
                          pending pause cleanly, resets UI to idle. */}
                      {isRunning && (
                        <button
                          type="button"
                          onClick={() => {
                            if (pendingPause?.pauseId) {
                              fetch('/api/jobotics-agent/resume', {
                                method: 'POST', credentials: 'include',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ pauseId: pendingPause.pauseId, choice: 'cancel' }),
                              }).catch(() => {});
                            }
                            if (sseControllerRef.current) {
                              try { sseControllerRef.current.abort(); } catch (_) {}
                            }
                            setIsRunning(false);
                            setPendingPause(null);
                          }}
                          className="ml-auto text-xs text-rose-600 hover:text-rose-800 hover:bg-rose-50 px-2 py-0.5 rounded border border-rose-200"
                          title="Cancel the current run"
                        >
                          Stop run
                        </button>
                      )}
                    </div>
                    {currentSteps.length > 0 && (
                      <div className="space-y-0">
                        {currentSteps.map((step, i) => (
                          <StepItem key={step.id} step={step} isLast={i === currentSteps.length - 1} />
                        ))}
                      </div>
                    )}
                    {streamPreview.trim().length > 0 && (
                      <div className={currentSteps.length > 0 ? 'mt-4 pt-3 border-t border-gray-200' : ''}>
                        <div className="flex items-center gap-2 text-xs font-medium text-amber-800 mb-2">
                          <Zap className="w-3.5 h-3.5" />
                          <span>Streaming output</span>
                          <span className="font-normal text-gray-500">— full text as it arrives (plain)</span>
                        </div>
                        <div
                          ref={streamPreviewScrollRef}
                          className="max-h-[min(70vh,36rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white px-3 py-2"
                        >
                          <pre className="whitespace-pre-wrap break-words font-mono text-xs text-gray-800 leading-relaxed m-0 tab-size-[2]">
                            {scrubResultEmailText(streamPreview, user?.email)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Plan confirmation */}
              {plan && plan.confirmable && (
                <div className="flex justify-start">
                  <div className="max-w-4xl w-full mr-12">
                    <ParamForm
                      plan={plan}
                      editedParams={editedParams}
                      setEditedParams={setEditedParams}
                      onConfirm={handleConfirm}
                      onSkip={handleSkip}
                      isRunning={isRunning}
                    />
                  </div>
                </div>
              )}

              {isPlanLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200 mr-12">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                      <span className="text-sm text-gray-600">Analyzing your question...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {!showHeroComposer && (
            <div className="border-t border-gray-200 p-4 shrink-0">
              {followUpSuggestions.length > 0 && !busy && (
                <div className="mb-3">
                  <button
                    type="button"
                    onClick={() => setFollowUpsOpen((v) => !v)}
                    className="flex items-center gap-1.5 mb-2 group hover:text-indigo-700 transition-colors"
                    aria-expanded={followUpsOpen}
                  >
                    {followUpsOpen
                      ? <ChevronDown className="w-3 h-3 text-indigo-500 group-hover:text-indigo-700" />
                      : <ChevronRight className="w-3 h-3 text-indigo-500 group-hover:text-indigo-700" />}
                    <span className="text-xs font-medium text-gray-500 group-hover:text-indigo-700">
                      Suggested follow-ups
                    </span>
                    <span className="text-xs text-gray-400">
                      ({followUpSuggestions.length})
                    </span>
                    {!followUpsOpen && (
                      <span className="text-xs text-indigo-500 group-hover:text-indigo-700">— show</span>
                    )}
                  </button>
                  {followUpsOpen && (
                    <div className="flex flex-wrap gap-2">
                      {followUpSuggestions.map((q, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => applySampleQuery(q)}
                          className="px-3 py-1.5 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-full border border-indigo-200 hover:border-indigo-300 transition-colors text-left"
                        >
                          {q}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={resetAgent}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" />
                        New chat
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!quickMode && attachedFiles.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {attachedFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded-lg text-xs">
                      <FileText className="w-3 h-3 text-indigo-600" />
                      <span className="text-indigo-700 truncate max-w-[140px]">{f.name}</span>
                      <button type="button" onClick={() => setAttachedFiles(prev => prev.filter((_, j) => j !== i))} className="text-indigo-400 hover:text-indigo-700">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Large-file (S3) staged uploads in the follow-up composer.
                  Without this the hero composer was the ONLY place a user
                  saw their queued files — picking a file in a follow-up
                  turn appeared to do nothing, then the agent said "I
                  don't see any attached files." Bug surfaced in the
                  MEP Contract Comparison Analyzer custom agent demo. */}
              {isLargeFileMode && (
                <LargeFileUploadChips
                  uploads={largeFileUploads}
                  onRemove={(tmpId) => {
                    largeFileReadyRef.current = largeFileReadyRef.current.filter((r) => r.tmpId !== tmpId);
                    setLargeFileUploads((prev) => prev.filter((p) => p.tmpId !== tmpId));
                  }}
                />
              )}

              <form onSubmit={handleSubmit} className="flex items-center gap-2">
                {!quickMode && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                    className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 hover:text-indigo-600 hover:border-indigo-300 disabled:opacity-40 transition-colors flex-shrink-0"
                    title="Attach file(s)"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setQuickMode((v) => !v)}
                  disabled={busy}
                  title={quickMode ? 'Quick mode on' : 'Quick mode'}
                  className={`inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium border transition-colors flex-shrink-0 disabled:opacity-40 ${
                    quickMode
                      ? 'bg-amber-50 border-amber-300 text-amber-900'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-amber-200'
                  }`}
                >
                  <Zap className="w-3.5 h-3.5" />
                  Quick
                </button>
                <div className="relative flex-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder={
                      // After the first turn we prefer a short "Follow up…"
                      // hint over the longer agent blurb; for the first turn
                      // on an agent page, surface the agent blurb as the
                      // contextual hint (mirrors the hero composer).
                      agentMeta && messages.length === 0
                        ? agentMeta.blurb
                        : quickMode
                          ? messages.length > 0
                            ? 'Follow up — MCAA, NECA, batch lines, or web…'
                            : 'Quick — MCAA / Weblem, NECA, batch labor, or web research…'
                          : messages.length > 0
                            ? 'Follow up — finance, ops, strategy…'
                          : 'Project question — finance, ops, strategy…'
                    }
                    className="w-full px-4 py-2.5 pr-12 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                    disabled={busy}
                  />
                </div>
                {/* Voice input (dictation into the follow-up bar) + Voice mode
                    orb (full-screen bidirectional voice). Mirror of the hero
                    composer's right-side controls so both surfaces have the
                    same affordances. */}
                <VoiceMicButton
                  getCurrentText={() => input}
                  onUpdate={(text) => setInput(text)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-indigo-200 bg-white hover:bg-indigo-50 flex-shrink-0"
                />
                <button
                  type="button"
                  onClick={() => setVoiceModeOpen(true)}
                  title="Open voice mode (bidirectional voice conversation)"
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors flex-shrink-0"
                >
                  <span className="flex items-end gap-[2px]" aria-hidden>
                    <span className="block w-[2px] h-2 bg-white rounded-full" />
                    <span className="block w-[2px] h-3 bg-white rounded-full" />
                    <span className="block w-[2px] h-4 bg-white rounded-full" />
                    <span className="block w-[2px] h-3 bg-white rounded-full" />
                    <span className="block w-[2px] h-2 bg-white rounded-full" />
                  </span>
                </button>
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  {isPlanLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Full-screen voice-mode overlay (bidirectional OpenAI Realtime).
          Mounted only when open so we don't pay the connection cost when the
          user isn't in voice mode. onTurn pushes each completed voice
          user→assistant pair into this page's `messages` state so the chat
          history surface reflects what was said via voice. */}
      <VoiceModeOverlay
        open={voiceModeOpen}
        onClose={() => setVoiceModeOpen(false)}
        agentId={agentId}
        agentName={agentMeta?.name || 'Jobotics Agent'}
        onTurn={({ user: userText, assistant: assistantText, agentId: turnAgentId }) => {
          // 1. Add to the in-memory chat thread so the UI shows the voice
          //    turn next to text turns.
          const baseId = Date.now();
          setMessages((prev) => [
            ...prev,
            {
              id: baseId,
              type: 'user',
              content: userText,
              timestamp: new Date().toISOString(),
              via: 'voice',
            },
            {
              id: baseId + 1,
              type: 'bot',
              content: assistantText,
              timestamp: new Date().toISOString(),
              via: 'voice',
            },
          ]);
          // 2. Persist to user_chat_messages (same table the text-chat
          //    persist-turn path writes to) so the turn survives reload and
          //    shows up in the History panel under the same session.
          persistJoboticsTurn({
            query: userText,
            assistantText,
            steps: null,
            quick: quickModeRef.current,
            via: 'voice',
            agentId: turnAgentId || null,
          }).catch(() => { /* warned upstream */ });
        }}
      />
    </div>
  );
};

export default JoboticsAgent;
